import { emitHintSignal } from '@flobi/asset-library/hints';
import { deltaToParent } from '../space';
import rotateCursor from '../../assets/tool-cursors/selection-rotate.png';
import {
  applyToPoint,
  clonerPlan,
  containsPoint,
  identity,
  inflate,
  ROOT_ID,
  intersects,
  invert,
  localGeometryBounds,
  multiply,
  snapDrag,
  growToCluster,
  growToNearest,
  orientedBounds,
  regionHolds,
  shrinkFromFurthest,
  rectBetween,
  rotateAbout,
  rotation,
  scaleAbout,
  translation,
  type Matrix,
  type NodeRecord,
  type PathPoint,
  type Rect,
  type Region,
  type SnapLine,
  atLength,
  lengthNearest,
  rulerIn,
  type Ruler,
  gapsAround,
  matchedGaps,
  formatLength,
  parseLength,
  type Neighbour, drawsAt, cropShowing, imagePaintMatrix, placePicture, type ImagePaint, type PictureCrop } from '@flobi/artwork-core';
import type { Overlay, Point, Rail, RailButton, Tool, ToolContext, Well } from './types';
import { reach as touchReach } from '../touchInput';
import { gripCursor, gripReadout, pulled, repeatHandleAt, repeatHandles, startPull, type RepeatGrip, type RepeatHandle, type RepeatPull } from './repeatHandles';

/** How near a corner handle the pointer has to be, in screen pixels. */
const HANDLE_REACH = 7;
/**
 * How far outside a corner still turns the shape, in screen pixels.
 *
 * Figma's bargain, and the right one: there is no knob to aim at. Just outside each
 * corner is a quarter of a ring where the pointer becomes a turn, which costs the canvas
 * nothing and is always exactly where the hand already is.
 */
const TURN_REACH = 22;
/** How near a radius grip counts as on it, in screen pixels. */
const RADIUS_REACH = 7;
/** Where a radius grip sits when the corner is square, in screen pixels from the corner. */
const RADIUS_REST = 14;
/** Turning with shift held goes round in steps of this many degrees. */
const TURN_STEP = 15;
/** How near an edge has to come before the drag is pulled into line, in screen pixels. */
/** How far around the chosen thing to look for something to measure to, in pixels. */
const MEASURE_REACH = 700;

const SNAP_REACH = 6;
/** How far past the dragged box to look for something to line up with, in screen pixels. */
const SNAP_LOOK = 600;

/** Which corner of the box a handle sits on. */
type Corner = 'nw' | 'ne' | 'sw' | 'se';
type Edge = 'n' | 'e' | 's' | 'w';
type ResizeHandle = Corner | Edge;

/** In the order the schema stores radii: top-left, top-right, bottom-right, bottom-left. */
const CORNERS = ['nw', 'ne', 'se', 'sw'] as const;
const EDGES = ['n', 'e', 's', 'w'] as const;
const inRect = (box: Rect, at: Point): boolean => at.x >= box.x && at.x <= box.x + box.width && at.y >= box.y && at.y <= box.y + box.height;
const OPPOSITE: Record<ResizeHandle, ResizeHandle> = { nw: 'se', ne: 'sw', se: 'nw', sw: 'ne', n: 's', e: 'w', s: 'n', w: 'e' };

/**
 * The pointer while it is over a corner's turn ring.
 *
 * The approved Round arrow cursor: white with a dark edge, with its hotspot in the
 * middle. Like the tool cursors, it is baked to a bitmap to avoid native SVG flicker.
 */
const TURN_CURSOR = `url("${rotateCursor}") 12 12, auto`;

/** The cursor for pulling a corner, which depends on which diagonal it is on. */
const RESIZE_CURSOR: Record<ResizeHandle, string> = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', n: 'ns-resize', e: 'ew-resize', s: 'ns-resize', w: 'ew-resize' };

/**
 * A selected node's box as it actually sits: its outline in its own coordinates, and
 * the matrix that puts those coordinates on the page. Once a shape has been turned, the
 * box the eye sees is this one — not the upright rectangle that contains it.
 */
interface Frame {
  readonly id: string;
  readonly local: Rect;
  readonly world: Matrix;
}

type Gesture =
  /**
   * `moving` turns true once the press has travelled DRAG_SLOP: a click that only chooses
   * never moves, snaps or settles anything (sel-F14). `copy` is option's copy, made at that
   * same moment rather than on the press, so an option-click leaves no hidden twin behind
   * (sel-F15). `drop` is a chosen layer shift-pressed, let go of only if the press ends
   * without a drag — a shift-drag carries the whole selection (sel-F21). `kept` is what was
   * chosen before the copies were made, which Esc goes back to (sel-F24).
   */
  /** One end of a line taken by the hand: the other end stays where it is (Farah, 2026-10-23). */
  | { kind: 'line-end'; id: string; fixed: Point; was: Matrix; length: number }
  | { kind: 'move'; from: Point; origins: Map<string, Point>; box: Rect | null; moving: boolean; copy: boolean; drop: string | null; kept: readonly string[] | null; applied?: { dx: number; dy: number }; enter?: string | null }
  /**
   * `kids` are an artboard's layers as the drag found them, moved back as its origin moves
   * so the art stays put (sel-F19). `was` is every property the drag can write, as it found
   * them, so Esc can put the layer back (sel-F24).
   */
  | { kind: 'resize'; frame: Frame; handle: ResizeHandle; fixed: Point; transform: Matrix; fontSize: number; kids: { id: string; transform: Matrix }[]; was: Record<string, unknown>; grab: Point; picture: FramedPicture | null; cropped: boolean }
  /** A picture carried about inside its frame. The frame stays put. */
  | { kind: 'picture-move'; frame: Frame; picture: FramedPicture; from: Point; was: Record<string, unknown> }
  /** Several layers resized in page space from their shared frame, once per gesture. */
  | { kind: 'selection-resize'; box: Rect; handle: ResizeHandle; fixed: Point; transforms: Map<string, Matrix>; grab: Point; boards?: Map<string, Rect> }
  /** `angle` is the one shape's own angle on the page at the press, or null for several. */
  | { kind: 'turn'; pivot: Point; from: number; transforms: Map<string, Matrix>; angle: number | null }
  | { kind: 'band'; from: Point; to: Point }
  | { kind: 'text' }
  /** Dragging one of the brackets that say where type sits on its path. */
  | { kind: 'ride'; id: string; which: 'start' | 'end' }
  /** An anchor or handle held; `moving` once the press has travelled far enough to be a drag. */
  | { kind: 'points'; from: Point; moving: boolean }
  /**
   * A box or a loop drawn to catch anchors rather than whole shapes.
   *
   * `tap` holds what the press would have meant had the hand not moved, so the same press
   * can end as either a click on something or a region round it. Every point cursor sets
   * it: without one, a press that never becomes a drag means NOTHING, and a person whose
   * drag catches no anchors is left clicking at a canvas that will not answer.
   *
   * `adopt` is the cursor a drag should leave in hand, for the one cursor where dragging
   * out a region IS another cursor's whole gesture.
   */
  | { kind: 'catch'; shape: 'box' | 'loop'; from: Point; to: Point; trail: Point[]; tap?: { hit: string | null }; adopt?: Mode }
  /**
   * The frame round a set of chosen anchors, worked like a shape's.
   *
   * `before` is the path as it was when the drag started: every frame transforms from
   * there rather than from the frame before, or a slow drag would compound its own
   * output and travel further than a fast one over the same distance.
   */
  | { kind: 'points-resize'; fixed: Point; from: Point; handle: ResizeHandle; box: Rect; angle: number; before: PathPoint[] }
  | { kind: 'points-turn'; pivot: Point; from: number; was: number; before: PathPoint[] }
  | { kind: 'points-move'; from: Point; before: PathPoint[]; moving: boolean }
  /** A corner's radius being pulled. `before` is every radius as the drag found them. */
  | { kind: 'radius'; id: string; corner: Corner; box: Rect; world: Matrix; before: [number, number, number, number]; from: Point; inset: number; moving: boolean }
  /**
   * A piece of path being pulled by its middle, which moves the anchors at both ends.
   *
   * `added` is set when the press made a point rather than taking hold of an existing
   * one, so letting go without moving still leaves that point behind — a click on a
   * segment adds an anchor, and a drag from one adds it and brings it with you.
   */
  | { kind: 'segment'; from: Point; before: PathPoint[]; added: boolean; moving: boolean }
  /** One of a repeat's handles pulled (`repeatHandles.ts`); `pull.was` puts it back. */
  | { kind: 'repeat'; pull: RepeatPull };

/**
 * What a press means, which is the thing the rail changes.
 *
 * Illustrator splits this across three tools in the toolbar — the black arrow, the white
 * arrow and the lasso — and then you have to know which is which. They are one tool here
 * because they are one question asked four ways: what did I just point at? The rail is
 * where the answer is chosen, so the toolbar keeps one cursor.
 */
type Mode = 'object' | 'point' | 'area' | 'lasso';

/** How far a catch has to be dragged before it is a region rather than a click. */
const CATCH_SLOP = 2;
/**
 * How far a press has to travel before it is a drag, in screen pixels.
 *
 * A hand moves a pixel or two between pressing and letting go, every time. Without this a
 * click that adds a point also nudges it, and the two segments either side change angle by
 * a hair — which is exactly what a click on the middle of a line must not do.
 */
const DRAG_SLOP = 3;

/**
 * The frame round a set of chosen anchors.
 *
 * `box` is in the frame's own turned axes and `corners` are where those corners land on
 * the page, which is the same bargain a shape's Frame makes with its `local` and `world`.
 */
interface PointFrame {
  /** Bounds of the chosen anchors and their editable handles. */
  readonly bounds: Rect;
  readonly box: Rect;
  readonly angle: number;
  /** Page coordinates into the frame's axes, for working out a drag's scale. */
  readonly toLocal: Matrix;
  readonly corners: Record<Corner, Point>;
  readonly centre: Point;
}

/**
 * Select: the tool everything else returns to.
 *
 * It is four gestures wearing one cursor — drag a handle to resize, drag a shape to
 * move it and everything else chosen with it, drag the empty canvas to draw a band
 * round things, and press on a path's anchor while its points are open. Which one it is
 * comes from what is under the press, which is why the order they are asked in matters:
 * handles sit on top of shapes, and shapes on top of the page.
 */
/**
 * Everything about a piece of type riding a path, measured where the type measures it.
 *
 * The ruler is in the TEXT node's coordinates, because that is the space its `start` and
 * `span` are counted in and the renderer's; the two transforms come back with it so the
 * overlay can put a bracket on the page and a press can be asked how far along it landed.
 */
interface Ride {
  readonly ruler: Ruler;
  readonly start: number;
  readonly span: number;
  /** A distance along the path, as a point on the page. */
  at(distance: number): Point | null;
  /** A point on the page, as a distance along the path. */
  distance(world: Point): number;
  /** How tall the brackets stand: the type's own size. */
  readonly size: number;
}

/**
 * The text cutting out the layer under a press, when the press is on its letters: a mask
 * clips the layers stacked above it in the same group, so the text is a sibling below.
 */
export function maskTextUnder(ctx: Pick<ToolContext, 'scene' | 'node'>, id: string, world: Point): string | null {
  const parent = ctx.scene.parentOf(id);
  if (!parent) return null;
  const siblings = ctx.scene.childrenOf(parent);
  const at = siblings.indexOf(id);
  for (let i = at - 1; i >= 0; i--) {
    const sibling = siblings[i]!;
    const node = ctx.node(sibling);
    if (node?.type !== 'text' || !node.isMask || !ctx.scene.choosable(sibling)) continue;
    if (drawsAt(ctx.scene, sibling, world, 0)) return sibling;
  }
  return null;
}

function rideOf(ctx: ToolContext, id: string): Ride | null {
  const node = ctx.node(id);
  if (node?.type !== 'text' || !node.onPath) return null;
  const path = ctx.node(node.onPath.id);
  if (path?.type !== 'path') return null;
  const sub = path.path?.subpaths?.[0];
  if (!sub || sub.points.length < 2) return null;
  const world = ctx.scene.worldTransform(id);
  const ruler = rulerIn(sub.points, sub.closed, ctx.scene.worldTransform(node.onPath.id), world);
  const back = invert(world);
  if (!ruler || !back) return null;
  const start = node.onPath.start ?? 0;
  return {
    ruler,
    start,
    span: node.onPath.span ?? Math.max(0, ruler.length - start),
    at: (distance) => {
      const place = atLength(ruler, distance);
      return place ? applyToPoint(world, place.x, place.y) : null;
    },
    distance: (at) => lengthNearest(ruler, applyToPoint(back, at.x, at.y)),
    size: node.style?.fontSize ?? 16,
  };
}

/** The bracket under a press, if one is within reach of it. */
function bracketAt(ctx: ToolContext, id: string, world: Point): 'start' | 'end' | null {
  const ride = rideOf(ctx, id);
  if (!ride) return null;
  const reach = 9 * ctx.unitsPerPixel;
  for (const which of ['start', 'end'] as const) {
    const at = ride.at(which === 'start' ? ride.start : ride.start + ride.span);
    if (at && Math.hypot(at.x - world.x, at.y - world.y) <= reach) return which;
  }
  return null;
}


/**
 * The layers a move, turn or nudge actually writes: the topmost of the selection, and not
 * locked. A layer inside a selected group moves with the group — moving it on its own too
 * carried it twice — and a batch is all or nothing, so one locked layer in it (a locked
 * background after Select All) used to stop everything moving, silently.
 */
/**
 * How far in from its own corner a point is, on the tighter of the two axes, in the
 * shape's own space — so the arc follows the hand rather than running ahead of it on
 * the long side.
 */
function insetOf(box: Rect, world: Matrix, corner: Corner, at: Point): number {
  const back = invert(world);
  if (!back) return 0;
  const here = applyToPoint(back, at.x, at.y);
  const inX = corner === 'nw' || corner === 'sw' ? here.x - box.x : box.x + box.width - here.x;
  const inY = corner === 'nw' || corner === 'ne' ? here.y - box.y : box.y + box.height - here.y;
  return Math.min(inX, inY);
}

/** True when `outer` holds all of `inner`, edges included. */
function encloses(outer: Rect, inner: Rect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.width <= outer.x + outer.width && inner.y + inner.height <= outer.y + outer.height;
}

/** An artboard at the top of the page that a hand may take whole: seen, and not locked. */
export function boardOnPage(scene: Pick<ToolContext['scene'], 'get' | 'parentOf' | 'lockedHere' | 'hiddenHere'>, id: string): boolean {
  return scene.get(id)?.type === 'artboard' && (scene.parentOf(id) ?? ROOT_ID) === ROOT_ID && !scene.lockedHere(id) && !scene.hiddenHere(id);
}

/**
 * A drag that carries nothing but whole artboards lifts them out of the canvas's tiles, so
 * each move slides one picture rather than redrawing everything under the boards.
 */
function liftIfBoards(ctx: ToolContext, carried: ReadonlyMap<string, unknown>): void {
  const ids = [...carried.keys()];
  if (ids.length > 0 && ids.every((id) => ctx.node(id)?.type === 'artboard' && (ctx.scene.parentOf(id) ?? ROOT_ID) === ROOT_ID)) ctx.liftBoards?.(ids);
}

export function movable(ctx: { scene: { topmost(ids: Iterable<string>): string[]; lockedHere(id: string): boolean } }, ids: readonly string[]): string[] {
  return ctx.scene.topmost(ids).filter((id) => !ctx.scene.lockedHere(id));
}

/** How far the press landed from the handle it took: a handle is a target, not a pixel. */
function grabOf(handle: Point, press: Point): Point {
  return { x: handle.x - press.x, y: handle.y - press.y };
}

/**
 * Where a handle being dragged lands: the HANDLE is lined up, not the pointer a few pixels
 * off it, so the edge sits exactly on the line it snapped to — and only on the axes that
 * handle moves (a bottom edge never lights an x line). Answered as the handle, which is
 * what the resize reads: handing back the pointer took the grab off again, so the edge
 * stopped as far past the line as the press was from it (Tarek, 3 Nov). `free` is ⌘:
 * the handle follows the hand and lines up with nothing.
 */
function handlePlace(ctx: ToolContext, world: Point, grab: Point, handle: ResizeHandle, ignore: string[], turned = false, free = false): Point {
  const at = { x: world.x + grab.x, y: world.y + grab.y };
  if (free) return at;
  // A turned shape's edge moves on a slant, across both of the page's axes.
  const axes = turned ? { x: true, y: true } : { x: handle.includes('w') || handle.includes('e'), y: handle.includes('n') || handle.includes('s') };
  return ctx.place(at, ignore, axes);
}

/**
 * An edge changes one axis; Shift grows the other around the opposite edge's midpoint.
 *
 * A handle pulled past the fixed side carries on past it: the box opens out on the other
 * side and `flipX`/`flipY` say it has turned over. It used to measure only the distance
 * from the fixed side, so the bottom edge pulled up through the top grew back DOWN while
 * the hand went up (Farah, 2026-10-23).
 */
/** A picture lying in a layer's frame, and how to write down that it has been laid somewhere else. */
interface FramedPicture {
  readonly assetId: string;
  /** Where all of it lies, in the layer's own units. */
  readonly placed: Rect;
  /** The fields that say which part shows when the layer's frame is `frame`. */
  showing(frame: Rect): Record<string, unknown>;
  /** The fields as they were. */
  readonly was: Record<string, unknown>;
}

/**
 * The picture a layer shows and where it lies, whether it is an image layer or a shape filled
 * with one, which every Generate result is. Null for anything else, a tiled fill, or a picture
 * whose size nothing knows yet: without that there is no saying where it lies.
 */
function framedPicture(ctx: ToolContext, id: string, frame: Rect): FramedPicture | null {
  const node = ctx.node(id) as (NodeRecord & { width?: number; height?: number; assetId?: string; fit?: string; crop?: PictureCrop; fills?: ImagePaint[] }) | undefined;
  if (!node || !(frame.width > 0) || !(frame.height > 0) || ctx.scene.lockedHere(id)) return null;
  if (node.type === 'image') {
    const size = node.assetId ? ctx.pictureSize?.(node.assetId) : null;
    if (!size || !node.assetId) return null;
    const whole = placePicture(frame, size, node.fit === 'fit' ? 'contain' : 'cover', node.crop);
    const placed = { ...whole, x: frame.x + whole.x, y: frame.y + whole.y };
    return { assetId: node.assetId, placed, showing: (to) => ({ crop: cropShowing(placed, to) }), was: { crop: node.crop ?? null } };
  }
  if (!['rect', 'ellipse', 'polygon', 'star'].includes(node.type)) return null;
  const fills = node.fills ?? [];
  const at = fills.findLastIndex((paint) => paint.type === 'image' && paint.visible !== false);
  const paint = fills[at];
  if (!paint || paint.fit === 'tile') return null;
  const size = ctx.pictureSize?.(paint.assetId);
  if (!size) return null;
  const [a, , , d, e, f] = imagePaintMatrix(paint, frame, size);
  const placed = { x: e, y: f, width: size.width * a, height: size.height * d };
  const { transform: _transform, ...rest } = paint;
  return { assetId: paint.assetId, placed, showing: (to) => ({ fills: fills.map((fill, index) => (index === at ? { ...rest, crop: cropShowing(placed, to) } : fill)) }), was: { fills } };
}

/** How far a picture may be carried along one axis: it never opens a gap it did not already have. */
function heldInFrame(at: number, start: number, size: number, frameAt: number, frameSize: number): number {
  const lo = size >= frameSize ? frameAt + frameSize - size : frameAt;
  const hi = size >= frameSize ? frameAt : frameAt + frameSize - size;
  return Math.min(Math.max(hi, start), Math.max(Math.min(lo, start), at));
}

function resizedBox(box: Rect, handle: ResizeHandle, fixed: Point, pointer: Point, proportional: boolean): Rect & { flipX: boolean; flipY: boolean } {
  const across = handle.includes('w') || handle.includes('e');
  const down = handle.includes('n') || handle.includes('s');
  // Which side of the fixed edge the handle started on, and which side the pointer is on.
  const startX = handle.includes('w') ? -1 : 1;
  const startY = handle.includes('n') ? -1 : 1;
  const sideX = across && pointer.x !== fixed.x ? Math.sign(pointer.x - fixed.x) : startX;
  const sideY = down && pointer.y !== fixed.y ? Math.sign(pointer.y - fixed.y) : startY;
  let width = across ? Math.max(1, Math.abs(pointer.x - fixed.x)) : box.width;
  let height = down ? Math.max(1, Math.abs(pointer.y - fixed.y)) : box.height;
  if (proportional && box.width > 0 && box.height > 0) {
    const ratio = box.width / box.height;
    if (!down) height = width / ratio;
    else if (!across) width = height * ratio;
    else if (width / height > ratio) height = width / ratio;
    else width = height * ratio;
  }
  return {
    x: across ? (sideX < 0 ? fixed.x - width : fixed.x) : fixed.x - width / 2,
    y: down ? (sideY < 0 ? fixed.y - height : fixed.y) : fixed.y - height / 2,
    width,
    height,
    flipX: across && sideX !== startX,
    flipY: down && sideY !== startY,
  };
}

export function selectTool(): Tool {
  let gesture: Gesture | null = null;
  let hoverHandle: ResizeHandle | null = null;
  /** The layer whose picture is being placed in its frame: its handles crop, a drag carries the picture. */
  let cropping: string | null = null;
  /** The repeat handle under the pointer, which grows and says what it sets. */
  let hoverGrip: RepeatGrip | null = null;
  /** The lines the last move agreed with, drawn until the drag ends. */
  let guides: readonly SnapLine[] = [];
  /** What a press means. Kept across tool changes, as a chosen mode should be. */
  let mode: Mode = 'object';
  /** An explicit insertion tool, so adding anchors does not depend on discovering midpoints. */
  let addingPoints = false;
  /** True while the pointer is on the point frame's edge, which lights it to say so. */
  let onFrameEdge = false;
  /**
   * The path under the pointer while a point cursor is in hand, and the anchor of it that
   * is within reach — on any shape, open or not.
   *
   * This is what makes coming near a point enough to take hold of it. Without it a person
   * has to choose the shape and then choose the point, which is two acts for one
   * intention, and the shape they are aiming at is usually obvious from where they are.
   */
  let hover: { id: string; index: number | null } | null = null;
  /**
   * The layer a click would take, outlined before the click: in a file of nested groups
   * the frame says in advance whether it is the group or the thing inside it, and with ⌘
   * held it follows the deepest layer, which is what a ⌘-click takes.
   */
  let pointedAt: string | null = null;
  /** The chosen group's layer a click would go into, outlined while the pointer is on it. */
  let pointedInside: string | null = null;
  /** A click has just gone into a group; the double-click it may belong to is answered. */
  let enteredByClick = false;
  /**
   * How the shape selection grew, so shrinking walks back the way it came.
   *
   * Growing takes the nearest, which means shrinking by any rule of its own can land
   * somewhere growing would never have been. Remembering what each grow started from
   * makes the pair a dial rather than two opinions. Anything else touching the selection
   * leaves the stack behind rather than letting it answer for a selection it never made.
   */
  let grown: { from: string[]; to: string[] }[] = [];
  const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && new Set(a).size === new Set([...a, ...b]).size;
  /**
   * How far the frame round a point selection has been turned, in degrees.
   *
   * Without this the box would be measured upright every frame, and turning the points
   * inside it would change its shape instead of turning it — the box would swell and
   * shrink as the selection swung round. It is measured in the turned frame instead, so
   * the box turns with what it holds. Reset whenever the selection is made afresh.
   */
  let frameAngle = 0;
  let frameSelection = '';
  let previewEnds = false;

  const unionOf = (boxes: readonly Rect[]): Rect | null => {
    let out: Rect | null = null;
    for (const box of boxes) {
      if (!out) {
        out = box;
        continue;
      }
      const x = Math.min(out.x, box.x);
      const y = Math.min(out.y, box.y);
      out = {
        x,
        y,
        width: Math.max(out.x + out.width, box.x + box.width) - x,
        height: Math.max(out.y + out.height, box.y + box.height) - y,
      };
    }
    return out;
  };

  const sizeOf = (node: NodeRecord): { width: number; height: number } => {
    const n = node as NodeRecord & { width?: number; height?: number; length?: number };
    if (node.type === 'line') return { width: n.length ?? 0, height: 0 };
    return { width: n.width ?? 0, height: n.height ?? 0 };
  };

  const frameOf = (ctx: ToolContext, id: string): Frame | null => {
    const node = ctx.node(id);
    if (!node) return null;
    const world = ctx.scene.worldTransform(id);
    const local = localGeometryBounds(node);
    if (local) return { id, local, world };

    // A group has no outline of its own: it is framed by the box round its children,
    // carried back into the group's own coordinates so everything downstream — the
    // corners, the knob, a resize — can treat it like any other frame.
    const box = ctx.boundsOf(id);
    const back = invert(world);
    if (!box || !back) return box ? { id, local: box, world: identity() } : null;
    // Each child's own outline, carried into the group's axes. Boxing the upright page
    // box instead made a turned group's frame far too big — twice the size at 45° — and
    // a corner resize then scaled from the wrong place (QA 2026-09-24, sel-F16).
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const walk = (record: NodeRecord, into: Matrix, depth: number) => {
      if (depth > 32 || record.visible === false) return;
      const own = localGeometryBounds(record);
      if (own) {
        for (const [cx, cy] of [
          [own.x, own.y],
          [own.x + own.width, own.y],
          [own.x, own.y + own.height],
          [own.x + own.width, own.y + own.height],
        ] as const) {
          const at = applyToPoint(into, cx, cy);
          minX = Math.min(minX, at.x);
          minY = Math.min(minY, at.y);
          maxX = Math.max(maxX, at.x);
          maxY = Math.max(maxY, at.y);
        }
      } else if (record.type === 'cloner') {
        // Frame the rendered copies, including effectors, not just the source child.
        for (const copy of clonerPlan(ctx.scene, record.id)) {
          const source = ctx.node(copy.sourceId);
          if (source) walk(source, multiply(multiply(into, copy.placement), source.transform), depth + 1);
        }
      } else {
        for (const child of ctx.scene.childrenOf(record.id)) {
          const source = ctx.node(child);
          if (source) walk(source, multiply(into, source.transform), depth + 1);
        }
      }
    };
    walk(node, identity(), 0);
    if (minX <= maxX) return { id, local: { x: minX, y: minY, width: maxX - minX, height: maxY - minY }, world };
    const corners = [
      applyToPoint(back, box.x, box.y),
      applyToPoint(back, box.x + box.width, box.y),
      applyToPoint(back, box.x, box.y + box.height),
      applyToPoint(back, box.x + box.width, box.y + box.height),
    ];
    const xs = corners.map((c) => c.x);
    const ys = corners.map((c) => c.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { id, local: { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y }, world };
  };

  /** The frame's four corners, where they are on the page. */
  const cornersOf = (frame: Frame): Record<Corner, Point> => {
    const { local: box, world } = frame;
    return {
      nw: applyToPoint(world, box.x, box.y),
      ne: applyToPoint(world, box.x + box.width, box.y),
      sw: applyToPoint(world, box.x, box.y + box.height),
      se: applyToPoint(world, box.x + box.width, box.y + box.height),
    };
  };

  const handlesOf = (frame: Frame): Record<ResizeHandle, Point> => {
    const corners = cornersOf(frame);
    const middle = (a: Point, b: Point): Point => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    return { ...corners, n: middle(corners.nw, corners.ne), e: middle(corners.ne, corners.se), s: middle(corners.sw, corners.se), w: middle(corners.nw, corners.sw) };
  };

  const resizeCursor = (frame: Frame | null, handle: ResizeHandle): string => {
    if (!frame || handle.length === 2) return RESIZE_CURSOR[handle];
    const axis = handle === 'e' || handle === 'w' ? frame.world.slice(0, 2) : frame.world.slice(2, 4);
    const direction = (Math.round(Math.atan2(axis[1]!, axis[0]!) / (Math.PI / 4)) + 4) % 4;
    return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][direction]!;
  };

  /** The size of what a repeat copies, as it stands on the page: what its Gap is measured from. */
  const sourceOf = (ctx: ToolContext, id: string): { width: number; height: number } | null => {
    const box = unionOf(ctx.scene.childrenOf(id).map((kid) => ctx.boundsOf(kid)).filter((b): b is Rect => !!b));
    return box ? { width: box.width, height: box.height } : null;
  };

  /**
   * The handles of the one repeat chosen, round the frame it is drawn with. None for a
   * locked one: every pull would be refused.
   */
  const gripsOf = (ctx: ToolContext): RepeatHandle[] => {
    if (ctx.selection.length !== 1 || ctx.points.active || ctx.text?.current) return [];
    const id = ctx.selection[0]!;
    if (ctx.node(id)?.type !== 'cloner' || ctx.scene.lockedHere(id)) return [];
    const frame = frameOf(ctx, id);
    return frame ? repeatHandles(ctx, id, frame.local, frame.world) : [];
  };

  /**
   * The radius grips of the one shape chosen, where they sit on the page.
   *
   * Only for the shapes that store a radius per corner — a rectangle and an artboard.
   * Each sits inside its corner, at the radius it already has or a little way in when the
   * corner is square, so there is always something to take hold of. They are left out of
   * a shape too small to hold four of them without them meeting in the middle.
   */
  const radiusGrips = (ctx: ToolContext): { corner: Corner; at: Point; radius: number }[] => {
    if (ctx.selection.length !== 1 || ctx.points.active) return [];
    const id = ctx.selection[0]!;
    const node = ctx.node(id) as (NodeRecord & { radii?: readonly number[] }) | undefined;
    if (!node || (node.type !== 'rect' && node.type !== 'artboard')) return [];
    const frame = frameOf(ctx, id);
    if (!frame) return [];
    const { local: box, world } = frame;
    const rest = RADIUS_REST * ctx.unitsPerPixel;
    // Two grips a side plus a gap, or they overlap and none of them can be aimed at.
    if (box.width < 4 * rest || box.height < 4 * rest) return [];
    const most = Math.min(box.width, box.height) / 2;
    return CORNERS.map((corner, index) => {
      const radius = Math.min(Math.max(0, node.radii?.[index] ?? 0), most);
      const inset = Math.min(Math.max(radius, rest), most);
      const x = corner === 'nw' || corner === 'sw' ? box.x + inset : box.x + box.width - inset;
      const y = corner === 'nw' || corner === 'ne' ? box.y + inset : box.y + box.height - inset;
      return { corner, at: applyToPoint(world, x, y), radius };
    });
  };

  /**
   * Is the pointer in the ring outside a corner, where a press turns the shape?
   *
   * Outside is the whole of it: inside the same radius is the corner handle's own reach,
   * and inside that again is the shape. The test is done in whatever axes the frame has,
   * so a shape that has already been turned is still turned from its own corners.
   */
  const turnCorner = (corners: Record<Corner, Point>, box: Rect, toLocal: Matrix, world: Point, unitsPerPixel: number): Corner | null => {
    const here = applyToPoint(toLocal, world.x, world.y);
    if (containsPoint(box, here.x, here.y)) return null;
    for (const key of CORNERS) {
      const away = Math.hypot(corners[key].x - world.x, corners[key].y - world.y) / unitsPerPixel;
      if (away > touchReach(HANDLE_REACH) && away <= touchReach(TURN_REACH)) return key;
    }
    return null;
  };

  const near = (a: Point, b: Point, reach: number) => Math.abs(a.x - b.x) <= reach && Math.abs(a.y - b.y) <= reach;

  /** Corners first, then the full length of each edge, within a constant screen reach. */
  /**
   * The layer one level inside a group that a click there would choose: the topmost child
   * that draws at the point, as going into the group picks it.
   */
  const layerInside = (ctx: ToolContext, group: string, world: Point): string | null => {
    if (ctx.node(group)?.type !== 'group') return null;
    const reach = 4 * ctx.unitsPerPixel;
    const kids = ctx.scene.childrenOf(group);
    for (let i = kids.length - 1; i >= 0; i--) {
      const kid = kids[i]!;
      const box = ctx.boundsOf(kid);
      if (!box || !containsPoint(inflate(box, reach), world.x, world.y)) continue;
      if (!ctx.scene.choosable(kid)) continue;
      if (drawsAt(ctx.scene, kid, world, reach)) return kid;
    }
    return null;
  };

  /** The end of a chosen line under the pointer, with the other end on the page, or null. */
  const lineEndAt = (ctx: ToolContext, id: string, world: Point): { id: string; fixed: Point; was: Matrix; length: number } | null => {
    const node = ctx.node(id) as (NodeRecord & { length?: number }) | undefined;
    if (node?.type !== 'line' || ctx.scene.lockedHere(id)) return null;
    const length = node.length ?? 0;
    const placed = ctx.scene.worldTransform(id);
    const start = applyToPoint(placed, 0, 0);
    const end = applyToPoint(placed, length, 0);
    const reach = touchReach(HANDLE_REACH) * ctx.unitsPerPixel;
    const was = [...node.transform] as Matrix;
    if (near(end, world, reach)) return { id, fixed: start, was, length };
    if (near(start, world, reach)) return { id, fixed: end, was, length };
    return null;
  };

  /**
   * The one line chosen, if that is what is chosen. A line has no box to pull: only its two
   * ends, which are points. Its frame has no height, so every press near it read as the
   * top or bottom edge and offered a height it does not have (Farah, 2026-10-23).
   */
  const loneLine = (ctx: ToolContext): string | null => {
    if (ctx.selection.length !== 1) return null;
    const id = ctx.selection[0]!;
    return ctx.node(id)?.type === 'line' ? id : null;
  };

  const handleAt = (ctx: ToolContext, frame: Frame | null, world: Point): ResizeHandle | null => {
    if (!frame) return null;
    if (loneLine(ctx) === frame.id) return null;
    const reach = touchReach(HANDLE_REACH) * ctx.unitsPerPixel;
    const corners = cornersOf(frame);
    // A box smaller on screen than two reaches is ALL corner: an icon at 10% zoom could
    // only be resized, never dragged. There a corner is taken only from outside the box,
    // and a press inside moves it (QA 2026-09-24, sel-F12).
    const across = Math.hypot(corners.ne.x - corners.nw.x, corners.ne.y - corners.nw.y);
    const down = Math.hypot(corners.sw.x - corners.nw.x, corners.sw.y - corners.nw.y);
    if (across < 2 * reach || down < 2 * reach) {
      const back = invert(frame.world);
      const here = back ? applyToPoint(back, world.x, world.y) : null;
      if (here && containsPoint(frame.local, here.x, here.y)) return null;
    }
    for (const corner of CORNERS) {
      if (near(corners[corner], world, reach)) return corner;
    }
    const segments: Record<Edge, [Point, Point]> = { n: [corners.nw, corners.ne], e: [corners.ne, corners.se], s: [corners.sw, corners.se], w: [corners.nw, corners.sw] };
    let closest: Edge | null = null;
    let distance = reach;
    for (const edge of EDGES) {
      const [from, to] = segments[edge];
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const lengthSquared = dx * dx + dy * dy;
      if (lengthSquared < 1e-12) continue;
      const along = ((world.x - from.x) * dx + (world.y - from.y) * dy) / lengthSquared;
      if (along < 0 || along > 1) continue;
      const away = Math.hypot(world.x - from.x - along * dx, world.y - from.y - along * dy);
      if (away <= distance) { closest = edge; distance = away; }
    }
    return closest;
  };

  const pointTransformFrame = (frame: PointFrame): Frame => ({ id: 'points', local: frame.box, world: rotation(frame.angle) });
  const pointHandleAt = (ctx: ToolContext, frame: PointFrame, world: Point): ResizeHandle | null =>
    ctx.points.hit(world) ? null : handleAt(ctx, pointTransformFrame(frame), world);
  const insidePointFrame = (frame: PointFrame, world: Point): boolean => {
    const local = applyToPoint(frame.toLocal, world.x, world.y);
    return containsPoint(frame.box, local.x, local.y);
  };

  /**
   * Everything chosen, and how far it is from what is around it.
   *
   * Held down rather than always on, because this is a question and not a state: most of
   * the time you are looking at the artwork, and numbers over the top of it are in the
   * way. Hold control and the drawing becomes a plan of itself — the size of what is
   * chosen, where it is, and the four distances to whatever it is nearest.
   *
   * It reads the same whether the hand is moving or still. A measurement you can only
   * see while dragging is one you cannot check afterwards, and checking afterwards is
   * most of what measuring is for.
   */
  const drawMeasures = (overlay: Overlay, ctx: ToolContext): void => {
    if (!ctx.modifiers.ctrl || ctx.selection.length === 0) return;
    const box = unionOf(ctx.selection.map((id) => ctx.boundsOf(id)).filter((one): one is Rect => !!one));
    if (!box) return;
    const unit = ctx.units;
    const per = ctx.unitsPerPixel;

    // What is around it, minus itself and anything it is carrying.
    const moving = new Set(ctx.selection);
    const look = MEASURE_REACH * per;
    const around = { x: box.x - look, y: box.y - look, width: box.width + look * 2, height: box.height + look * 2 };
    const others: Neighbour[] = [];
    for (const id of ctx.snapNeighbours(around, [...moving])) {
      const kind = ctx.node(id)?.type;
      // An artboard is what HOLDS things, not one of them, and a slice is a question
      // asked of the page rather than a thing on it. Neither is something to be a
      // certain distance from.
      if (!kind || kind === 'artboard' || kind === 'slice') continue;
      const rect = ctx.boundsOf(id);
      if (rect) others.push({ id, rect });
    }
    /**
     * Guides count as things to be a distance from, the same as shapes.
     *
     * "Nine millimetres from that guide" is exactly the measurement somebody puts a
     * guide down in order to make, and a guide you could snap to but never measure to
     * was answering half the question. A guide is a line rather than a box, so it goes
     * in as one with no thickness, stretched along the run it shares with what is
     * chosen — which is what makes `gapsAround` see it on the two sides it can actually
     * be on, and on neither of the other two.
     */
    for (const guide of ctx.guides) {
      others.push({
        id: `guide:${guide.axis}:${guide.at}`,
        rect:
          guide.axis === 'x'
            ? { x: guide.at, y: box.y - look, width: 0, height: box.height + look * 2 }
            : { x: box.x - look, y: guide.at, width: box.width + look * 2, height: 0 },
      });
    }

    const board = ctx.artboardAt({ x: box.x + box.width / 2, y: box.y + box.height / 2 });
    const holder = board ? ctx.boundsOf(board) : null;

    const gaps = gapsAround(box, others, holder);
    const even = matchedGaps(gaps, 0.5 * per);
    const tick = 4 * per;
    for (const gap of gaps) {
      const across = gap.side === 'left' || gap.side === 'right';
      const from = across ? { x: gap.from, y: gap.at } : { x: gap.at, y: gap.from };
      const to = across ? { x: gap.to, y: gap.at } : { x: gap.at, y: gap.to };
      // A gap of nothing is two edges touching, which the line would draw as a dot.
      if (gap.distance > 0.01) overlay.line(from, to, { width: 1 });
      for (const end of [from, to]) {
        if (across) overlay.line({ x: end.x, y: end.y - tick }, { x: end.x, y: end.y + tick }, { width: 1 });
        else overlay.line({ x: end.x - tick, y: end.y }, { x: end.x + tick, y: end.y }, { width: 1 });
      }
      const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
      // Two equal gaps are the thing people are usually checking for, and two equal
      // numbers side by side still have to be compared. Saying it is the measurement.
      const said = formatLength(gap.distance, unit, { compact: true }) + (even.has(gap.side) ? ' =' : '');
      overlay.label(middle, said, { align: 'center', baseline: 'middle', offset: across ? { x: 0, y: -9 } : { x: 10, y: 0 } });
    }

    // How big it is, under it; and where it is, above it. Both outside the box, so they
    // never sit on top of the thing being measured.
    overlay.label(
      { x: box.x + box.width / 2, y: box.y + box.height },
      `${formatLength(box.width, unit, { compact: true, bare: true })} × ${formatLength(box.height, unit, { compact: true })}`,
      { align: 'center', baseline: 'top', offset: { x: 0, y: 10 } },
    );
    overlay.label(
      { x: box.x, y: box.y },
      `${formatLength(box.x, unit, { compact: true, bare: true })}, ${formatLength(box.y, unit, { compact: true, bare: true })}`,
      { align: 'left', baseline: 'bottom', offset: { x: 0, y: -10 }, tone: 'quiet' },
    );
  };

  /** The centre of everything chosen, which is what a turn goes round. */
  /**
   * The selection's frame: the one shape's own, or for several, one box round
   * all of them — drawn and used for both resizing and turning. The ring used
   * to sit only round whichever shape was chosen first, with nothing on screen to say
   * which (QA 2026-09-24, sel-F20).
   */
  const selectionFrameOf = (ctx: ToolContext): Frame | null => {
    if (ctx.selection.length === 1) return frameOf(ctx, ctx.selection[0]!);
    const box = unionOf(ctx.selection.map((id) => ctx.boundsOf(id)).filter((b): b is Rect => !!b));
    return box ? { id: '', local: box, world: identity() } : null;
  };

  const pivotOf = (ctx: ToolContext): Point | null => {
    const box = unionOf(ctx.selection.map((id) => ctx.boundsOf(id)).filter((b): b is Rect => !!b));
    return box ? { x: box.x + box.width / 2, y: box.y + box.height / 2 } : null;
  };

  /**
   * What the pointer should look like where it is, asked in the order a press is.
   *
   * Radius grip, then corner, then the turn ring outside it — the same order, so the
   * cursor never promises something the press will not do.
   */
  const cursorFor = (ctx: ToolContext, world: Point): string | null => {
    if (addingPoints && ctx.points.active) return ctx.points.segmentNear(world) && !ctx.points.hit(world) ? 'copy' : 'crosshair';
    if (mode === 'lasso') return 'crosshair';
    const held = croppedPicture(ctx);
    const inverse = held ? invert(held.frame.world) : null;
    if (held && inverse) {
      const handle = handleAt(ctx, held.frame, world);
      if (handle) return resizeCursor(held.frame, handle);
      const local = applyToPoint(inverse, world.x, world.y);
      return inRect(held.frame.local, local) || inRect(held.picture.placed, local) ? 'move' : null;
    }
    const grip = repeatHandleAt(ctx, gripsOf(ctx), world);
    if (grip) return gripCursor(grip);
    if (ctx.points.active && ctx.points.hit(world)) return null;
    const frame = pointFrame(ctx);
    if (frame) {
      const handle = pointHandleAt(ctx, frame, world);
      if (handle) return resizeCursor(pointTransformFrame(frame), handle);
      if (turnCorner(frame.corners, frame.box, frame.toLocal, world, ctx.unitsPerPixel)) return TURN_CURSOR;
      if (insidePointFrame(frame, world)) return ctx.modifiers.shift ? 'crosshair' : 'move';
    }
    // On a piece of path: the middle offers a point, the rest offers the piece.
    if (mode !== 'object' && ctx.points.active) {
      const over = ctx.points.segmentNear(world);
      if (over) return near(over.middle, world, touchReach(RADIUS_REACH) * ctx.unitsPerPixel) ? 'copy' : 'move';
      return null;
    }
    if (ctx.selection.length === 0 || ctx.points.active) return null;
    if (ctx.selection.length === 1) {
      if (radiusGrips(ctx).some((grip) => near(grip.at, world, touchReach(RADIUS_REACH) * ctx.unitsPerPixel))) return 'pointer';
    }
    const line = loneLine(ctx);
    // An end is a point to carry anywhere; 'crosshair' here would be the plain tool cursor.
    if (line) return lineEndAt(ctx, line, world) ? 'move' : null;
    const first = selectionFrameOf(ctx);
    const handle = handleAt(ctx, first, world);
    if (handle) return resizeCursor(first, handle);
    const back = first ? invert(first.world) : null;
    // Over a board's name the press takes the board, not the turn ring it sits in.
    if (ctx.boardLabelAt?.(world)) return null;
    if (first && back && turnCorner(cornersOf(first), first.local, back, world, ctx.unitsPerPixel)) return TURN_CURSOR;
    return null;
  };

  /**
   * Reaching further from what is chosen, or pulling back from it.
   *
   * Over anchors the question is exact — the anchor next to this one — and the path
   * answers it. Over shapes it is a question about a page, and "near" has no meaning until
   * it is measured against the things being asked about: the engine's reach is a fraction
   * of the smaller of two shapes, so a poster and a full stop each set their own scale.
   *
   * It only ever looks at what shares a parent with something chosen. Growing that climbed
   * into a group, or out of one, would be a selection nobody could predict.
   */
  const reach = (ctx: ToolContext, how: 'grow' | 'whole' | 'shrink' | 'invert'): void => {
    if (ctx.points.active) {
      if (how === 'grow') ctx.points.grow();
      else if (how === 'whole') ctx.points.takeAll();
      else if (how === 'shrink') ctx.points.shrink();
      else ctx.points.invert();
      ctx.railChanged();
      ctx.refresh();
      return;
    }
    const parents = new Set(ctx.selection.map((id) => ctx.scene.parentOf(id) ?? ROOT_ID));
    if (parents.size === 0) parents.add(ROOT_ID);
    const candidates = [...parents].flatMap((parent) => [...ctx.scene.childrenOf(parent)]).filter((id) => ctx.scene.choosable(id));
    if (candidates.length === 0) return;

    if (how === 'invert') {
      const was = new Set(ctx.selection);
      grown = [];
      ctx.setSelection(candidates.filter((id) => !was.has(id)));
      return;
    }
    if (ctx.selection.length === 0) return;

    if (how === 'shrink') {
      // Back the way it came, when a grow is what put us here.
      const top = grown[grown.length - 1];
      if (top && same(top.to, ctx.selection)) {
        grown.pop();
        ctx.setSelection(top.from.filter((id) => !!ctx.node(id)));
        return;
      }
    }
    const boxes = candidates.map((id) => ctx.boundsOf(id) ?? { x: 0, y: 0, width: 0, height: 0 });
    const chosen = candidates.flatMap((id, index) => (ctx.selection.includes(id) ? [index] : []));
    const next =
      how === 'grow'
        ? growToNearest(chosen, boxes)
        : how === 'whole'
          ? growToCluster(chosen, boxes)
          : shrinkFromFurthest(chosen, boxes);
    const ids = next.map((index) => candidates[index]!);
    if (how === 'grow' || how === 'whole') {
      if (ids.length !== ctx.selection.length) grown.push({ from: [...ctx.selection], to: ids });
    } else {
      grown = [];
    }
    ctx.setSelection(ids);
  };

  /**
   * Everything beside the selection that matches it in one respect.
   *
   * Illustrator's Select Same, and the reason it earns a slot: on a page of fifty things
   * the question is almost never "what is near this" but "what else is like this". Fill
   * first, because that is what a restyle is about; size behind command, because it is the
   * same question asked of a different property.
   *
   * Matching is exact on colour and proportional on size — a fiftieth of the shape either
   * way — since two rectangles a pixel apart in height are the same size to everyone but a
   * comparison of floats.
   */
  const sameAs = (ctx: ToolContext, by: 'fill' | 'size'): void => {
    if (ctx.selection.length === 0) return;
    const parents = new Set(ctx.selection.map((id) => ctx.scene.parentOf(id) ?? ROOT_ID));
    const candidates = [...parents].flatMap((parent) => [...ctx.scene.childrenOf(parent)]).filter((id) => ctx.scene.choosable(id));

    grown = [];
    if (by === 'fill') {
      /**
       * Every visible paint a layer wears, as one key — or null for a layer that wears none
       * of its own (a group, a slice, a cloner). Only the first solid colour used to count,
       * so gradients, pictures, text, groups and slices all read as 'no fill' and matched
       * each other (QA 2026-09-24, sel-N2).
       */
      const keyOf = (id: string): string | null => {
        const node = ctx.node(id) as { type?: string; fills?: Record<string, unknown>[]; style?: { fills?: Record<string, unknown>[] } } | undefined;
        if (!node || ['group', 'artboard', 'slice', 'cloner', 'blend', 'effector'].includes(node.type ?? '')) return null;
        const paints = ((node.type === 'text' ? node.style?.fills : node.fills) ?? []).filter((paint) => paint.visible !== false);
        if (node.type === 'image') return `image:${JSON.stringify((node as { assetId?: string }).assetId ?? null)}`;
        return JSON.stringify(paints.map(({ visible: _visible, ...paint }) => paint));
      };
      const wanted = new Set(ctx.selection.map(keyOf).filter((key): key is string => key !== null));
      ctx.setSelection(candidates.filter((id) => {
        const key = keyOf(id);
        return key !== null && wanted.has(key);
      }));
      return;
    }
    const sizes = ctx.selection.map((id) => ctx.boundsOf(id)).filter((box): box is Rect => !!box);
    if (sizes.length === 0) return;
    ctx.setSelection(
      candidates.filter((id) => {
        const box = ctx.boundsOf(id);
        if (!box) return false;
        return sizes.some((want) => {
          const slack = Math.max(want.width, want.height) / 50;
          return Math.abs(box.width - want.width) <= slack && Math.abs(box.height - want.height) <= slack;
        });
      }),
    );
  };

  /**
   * The path under the pointer and, if the hand is near one, which of its anchors.
   *
   * Only the paths whose boxes come near the pointer are asked, so a page of ten thousand
   * shapes costs the same as a page of three: the index answers where to look before any
   * anchor is measured.
   */
  const pathUnder = (ctx: ToolContext, world: Point): { id: string; index: number | null } | null => {
    const reach = touchReach(HANDLE_REACH) * ctx.unitsPerPixel;
    const around: Rect = { x: world.x - reach, y: world.y - reach, width: 2 * reach, height: 2 * reach };
    // Visible and not locked anywhere above, as a region asks too (select-points-F19).
    const paths = ctx.nearby(around).filter((id) => ctx.node(id)?.type === 'path' && ctx.scene.choosable(id));
    const anchor = ctx.points.anchorNear(world, paths);
    if (anchor) return { id: anchor.id, index: anchor.index };
    const hit = ctx.hit(world);
    return hit && ctx.node(hit)?.type === 'path' ? { id: hit, index: null } : null;
  };

  const bandOf = (from: Point, to: Point): Rect => rectBetween(from, to);

  /** Every path a region could be about: the one already open, or all of them. */
  const catchable = (ctx: ToolContext): string[] => {
    const open = ctx.points.editingId;
    if (open) return [open];
    // Only what a hand could reach: a hidden path, or one inside a locked group, used to be
    // caught and then reshaped (QA 2026-09-25, select-points-F19).
    return ctx.scene.paintOrder().filter((id) => ctx.node(id)?.type === 'path' && ctx.scene.choosable(id));
  };

  /**
   * What a region caught, and on which path.
   *
   * Points belong to one path at a time, so a region drawn across two shapes has to
   * choose: it takes whichever caught the most, and the topmost of those when they tie —
   * which is the one the eye was on, because paintOrder ends at the front.
   */
  const caughtBy = (ctx: ToolContext, region: Region): { id: string; indices: number[] } | null => {
    let best: { id: string; indices: number[] } | null = null;
    for (const id of catchable(ctx)) {
      const anchors = ctx.points.anchorsOf(id);
      if (!anchors) continue;
      const indices = anchors.filter((anchor) => regionHolds(region, anchor.at)).map((anchor) => anchor.index);
      if (indices.length === 0) continue;
      if (!best || indices.length >= best.indices.length) best = { id, indices };
    }
    return best;
  };

  /** The region a catch gesture describes, in document coordinates. */
  const regionOf = (catching: Extract<Gesture, { kind: 'catch' }>): Region =>
    catching.shape === 'box'
      ? { kind: 'rect', rect: rectBetween(catching.from, catching.to) }
      : { kind: 'loop', points: catching.trail };

  /** Every multi-point selection gets a transform frame while point editing stays active. */
  const pointFrame = (ctx: ToolContext): PointFrame | null => {
    if (!ctx.points.active) { frameSelection = ''; return null; }
    const editing = ctx.points.preview();
    if (!editing) return null;
    const key = `${editing.id}:${editing.selected.slice().sort((a, b) => a - b).join(',')}`;
    if (key !== frameSelection) { frameAngle = 0; frameSelection = key; }
    const anchors = ctx.points.chosenAnchors();
    if (anchors.length < 2) return null;

    // Measured along the frame's own axes, so turning what it holds turns the box rather
    // than re-measuring it upright and changing its shape. Corners go back out the same way.
    // Handles transform with their anchors. Include them so a curved path whose
    // anchors happen to be collinear can still be resized in both directions.
    const world = ctx.scene.worldTransform(editing.id);
    const controls = editing.selected.flatMap(index => {
      const point = editing.points[index];
      return point ? [point.in, point.out].flatMap(handle => handle ? [applyToPoint(world, ...handle)] : []) : [];
    });
    const bounds = orientedBounds([...anchors, ...controls], frameAngle);
    if (!bounds) return null;
    // Ten screen pixels leave anchors reachable and give flat selections a usable frame.
    // Resizing still scales from the true bounds, without a jump at pointer-down.
    const box = inflate(bounds, 10 * ctx.unitsPerPixel);
    const toLocal = rotation(-frameAngle);
    const out = rotation(frameAngle);
    const place = (at: Point) => applyToPoint(out, at.x, at.y);

    return {
      bounds,
      box,
      angle: frameAngle,
      toLocal,
      corners: {
        nw: place({ x: box.x, y: box.y }),
        ne: place({ x: box.x + box.width, y: box.y }),
        sw: place({ x: box.x, y: box.y + box.height }),
        se: place({ x: box.x + box.width, y: box.y + box.height }),
      },
      centre: place({ x: box.x + box.width / 2, y: box.y + box.height / 2 }),
    };
  };

  /**
   * Ends a gesture that did not end with a release — another tool picked mid-drag, a
   * second finger landing, the system taking the touch. What moved is settled into its
   * artboard and the undo step closed, exactly as a release would; left open, the next
   * edit joined it and one Cmd+Z took back both (QA 2026-09-24, sel-F23).
   */
  const setCropping = (ctx: ToolContext, id: string | null): void => {
    if (cropping === id) return;
    cropping = id;
    ctx.croppingChanged?.(id);
    ctx.refresh();
  };

  /** Still the one layer chosen, and still a picture: anything else that happened ends it. */
  const croppedPicture = (ctx: ToolContext): { frame: Frame; picture: FramedPicture } | null => {
    if (!cropping) return null;
    const frame = ctx.selection.length === 1 && ctx.selection[0] === cropping ? frameOf(ctx, cropping) : null;
    const picture = frame ? framedPicture(ctx, frame.id, frame.local) : null;
    return frame && picture ? { frame, picture } : null;
  };

  /** A press on a resize handle of the one layer chosen. */
  const startResize = (ctx: ToolContext, frame: Frame, handle: ResizeHandle, world: Point): void => {
    const node = ctx.node(frame.id);
    if (!node) return;
    // The opposite corner or edge midpoint stays fixed in the shape's own axes.
    const localHandles = handlesOf({ ...frame, world: identity() });
    // The type size as the drag began, so scaling it stays a factor of where it
    // STARTED rather than compounding a little on every frame of the drag.
    const fontSize = (node as { style?: { fontSize?: number } }).style?.fontSize ?? 0;
    const kids =
      node.type === 'artboard'
        ? ctx.scene.childrenOf(frame.id).flatMap((id) => {
            const t = ctx.node(id)?.transform;
            return t ? [{ id, transform: [...t] as Matrix }] : [];
          })
        : [];
    const stored = node as NodeRecord & Record<string, unknown>;
    const was: Record<string, unknown> = { transform: [...node.transform] };
    for (const key of ['width', 'height', 'length', 'style']) if (stored[key] !== undefined) was[key] = stored[key];
    if (node.type === 'text') was.sizing = stored.sizing ?? 'auto-width';
    const picture = framedPicture(ctx, frame.id, frame.local);
    if (picture) Object.assign(was, picture.was);
    gesture = { kind: 'resize', frame, handle, fixed: localHandles[OPPOSITE[handle]], transform: [...node.transform] as Matrix, fontSize, kids, was, grab: grabOf(applyToPoint(frame.world, localHandles[handle].x, localHandles[handle].y), world), picture, cropped: false };
  };

  const interrupt = (ctx: ToolContext): void => {
    const was = gesture;
    gesture = null;
    ctx.liftBoards?.(null);
    guides = [];
    ctx.catchGuides([]);
    if (!was) return;
    if (was.kind === 'move' && was.moving) {
      const moved = [...was.origins].some(([id, from]) => {
        const t = ctx.node(id)?.transform;
        return t !== undefined && (t[4] !== from.x || t[5] !== from.y);
      });
      if (moved) ctx.settleIntoArtboards([...was.origins.keys()]);
    }
    if (was.kind === 'points') ctx.points.release();
    if (was.kind !== 'text') ctx.stopCapturing();
  };

  /**
   * Esc mid-drag: everything the gesture wrote goes back to how the press found it, and
   * the selection stays. Esc used to fall through to deselecting while the shape went on
   * following the pointer (QA 2026-09-24, sel-F24).
   */
  const cancel = (ctx: ToolContext): void => {
    const was = gesture;
    if (!was) return;
    const ops: unknown[] = [];
    if (was.kind === 'move' && was.moving) {
      if (was.kept) {
        // Option's copies: the drag made them, so calling it off takes them away.
        for (const id of was.origins.keys()) if (ctx.node(id)) ops.push({ op: 'delete', id });
      } else {
        for (const [id, from] of was.origins) {
          const t = ctx.node(id)?.transform;
          if (t) ops.push({ op: 'set', id, props: { transform: [t[0], t[1], t[2], t[3], from.x, from.y] } });
        }
      }
    } else if (was.kind === 'turn' || was.kind === 'selection-resize') {
      for (const [id, transform] of was.transforms) if (ctx.node(id)) ops.push({ op: 'set', id, props: { transform } });
    } else if (was.kind === 'picture-move') {
      if (ctx.node(was.frame.id)) ops.push({ op: 'set', id: was.frame.id, props: was.was });
    } else if (was.kind === 'resize') {
      ops.push({ op: 'set', id: was.frame.id, props: was.was });
      for (const kid of was.kids) if (ctx.node(kid.id)) ops.push({ op: 'set', id: kid.id, props: { transform: kid.transform }, force: true });
    } else if (was.kind === 'line-end') {
      if (ctx.node(was.id)) ops.push({ op: 'set', id: was.id, props: { transform: was.was, length: was.length } });
    } else if (was.kind === 'radius' && was.moving) {
      ops.push({ op: 'set', id: was.id, props: { radii: was.before } });
    } else if (was.kind === 'repeat' && ctx.node(was.pull.id)) {
      ops.push({ op: 'set', id: was.pull.id, props: { ...was.pull.was } });
    } else if (was.kind === 'points-resize' || was.kind === 'points-turn' || (was.kind === 'points-move' && was.moving)) {
      ctx.points.transformChosen(was.before, identity());
      if (was.kind === 'points-turn') frameAngle = was.was;
    }
    if (ops.length > 0) ctx.edit(ops);
    if (was.kind === 'move' && was.kept) ctx.setSelection(was.kept.filter((id) => !!ctx.node(id)));
    interrupt(ctx);
    ctx.refresh();
  };

  /** Opens a path's points when exactly one path is chosen, which is what a mode wants. */
  const openChosenPath = (ctx: ToolContext): void => {
    if (ctx.points.active || ctx.selection.length !== 1) return;
    const id = ctx.selection[0]!;
    if (ctx.node(id)?.type === 'path') ctx.points.open(id);
  };

  return {
    id: 'select',

    /**
     * A drag holds its undo step until it ends, so Esc can take back what it wrote inside
     * the same step rather than as a second one (sel-F24).
     */
    holdsStep: () => gesture !== null,

    onKeyDown(event, ctx) {
      if (cropping && !gesture && (event.key === 'Escape' || event.key === 'Enter')) {
        setCropping(ctx, null);
        return true;
      }
      if (event.key !== 'Escape' || !gesture) return false;
      event.preventDefault();
      cancel(ctx);
      return true;
    },

    onPointerDown(event, world, ctx) {
      // A gesture still open here lost its release somewhere; end it before starting
      // another, or this press would join its undo step (sel-F23).
      if (gesture) interrupt(ctx);
      enteredByClick = false;
      if (cropping) {
        const held = croppedPicture(ctx);
        const back = held ? invert(held.frame.world) : null;
        if (held && back) {
          const handle = handleAt(ctx, held.frame, world);
          if (handle) {
            startResize(ctx, held.frame, handle, world);
            return true;
          }
          const from = applyToPoint(back, world.x, world.y);
          // The faded part of the picture is as much the picture as the part that shows.
          if (inRect(held.frame.local, from) || inRect(held.picture.placed, from)) {
            gesture = { kind: 'picture-move', frame: held.frame, picture: held.picture, from, was: held.picture.was };
            return true;
          }
        }
        setCropping(ctx, null);
      }
      if (addingPoints && ctx.points.active) {
        const over = ctx.points.segmentNear(world);
        // Never add an overlapping anchor or turn a miss into a marquee/exit gesture.
        if (over && !ctx.points.hit(world)) {
          ctx.stopCapturing();
          ctx.points.addPointAt(over, 'here');
          ctx.stopCapturing();
          frameAngle = 0;
          ctx.railChanged();
          ctx.refresh();
        }
        return true;
      }
      // Explicit Lasso owns the whole drag, including presses over anchors, segments,
      // and the selection frame. Those targets must not turn a loop into a path edit.
      if (mode === 'lasso') {
        gesture = { kind: 'catch', shape: 'loop', from: world, to: world, trail: [world], tap: { hit: ctx.hit(world) } };
        return true;
      }
      // Anchors and Bézier handles always win. Edges resize, outside corners rotate,
      // and the interior carries the selected points together without changing selection.
      const frame = pointFrame(ctx);
      if (frame && !ctx.points.hit(world)) {
        const before = ctx.points.snapshot();
        const handle = pointHandleAt(ctx, frame, world);

        if (before && !handle && turnCorner(frame.corners, frame.box, frame.toLocal, world, ctx.unitsPerPixel)) {
          ctx.stopCapturing();
          const pivot = frame.centre;
          gesture = {
            kind: 'points-turn',
            pivot,
            from: Math.atan2(world.y - pivot.y, world.x - pivot.x),
            was: frame.angle,
            before,
          };
          return true;
        }
        if (before && handle) {
          ctx.stopCapturing();
          const b = frame.bounds;
          const fixed = { x: handle.includes('w') ? b.x + b.width : handle.includes('e') ? b.x : b.x + b.width / 2, y: handle.includes('n') ? b.y + b.height : handle.includes('s') ? b.y : b.y + b.height / 2 };
          const from = applyToPoint(frame.toLocal, world.x, world.y);
          gesture = { kind: 'points-resize', handle, fixed, from, box: b, angle: frame.angle, before };
          return true;
        }
        if (before && insidePointFrame(frame, world)) {
          // Shift keeps an additive marquee available inside an existing selection.
          gesture = event.shiftKey
            ? { kind: 'catch', shape: 'box', from: world, to: world, trail: [world] }
            : { kind: 'points-move', from: world, before, moving: false };
          ctx.setCursor(event.shiftKey ? 'crosshair' : 'move');
          return true;
        }
      }

      // An anchor within reach is taken hold of straight away, on whatever shape it
      // belongs to. Opening its path first is bookkeeping, not a decision, so it happens
      // here rather than being asked of the hand.
      if (mode !== 'object') {
        const near = pathUnder(ctx, world);
        if (near?.index !== null && near !== null && ctx.points.editingId !== near.id) {
          ctx.setSelection([near.id]);
          ctx.points.open(near.id);
          frameAngle = 0;
        }
      }

      // A path with its points open keeps the press while it lands on one.
      if (ctx.points.active) {
        if (ctx.points.press(world, event.shiftKey)) {
          gesture = { kind: 'points', from: world, moving: false };
          ctx.railChanged();
          return true;
        }

        // Not an anchor, but on the path itself. The point on offer at the middle adds an
        // anchor; anywhere else along the piece takes the piece itself, both its ends, so
        // a drag moves that stretch of path and a click just chooses it.
        const over = mode !== 'object' ? ctx.points.segmentNear(world) : null;
        if (over) {
          const onMiddle = near(over.middle, world, touchReach(RADIUS_REACH) * ctx.unitsPerPixel);
          if (onMiddle) ctx.points.addPointAt(over);
          else ctx.points.chooseSegment(over.index, event.shiftKey);
          const before = ctx.points.snapshot();
          if (before) gesture = { kind: 'segment', from: world, before, added: onMiddle, moving: false };
          frameAngle = 0;
          ctx.railChanged();
          return true;
        }
        // In object mode the press has left the path, so the path is done with. The point
        // modes stay in it: that is what being in them means.
        if (mode === 'object') ctx.points.close();
      }

      if (mode !== 'object') {
        // From the point cursor a press is still undecided: let go where you pressed and
        // it is a click on whatever is under it; drag and it is a box round points, which
        // is the mode it then leaves you in. Dragging out a selection IS that mode, so
        // making the person go and choose it first would be asking twice.
        gesture = {
          kind: 'catch',
          shape: 'box',
          from: world,
          to: world,
          trail: [world],
          tap: { hit: ctx.hit(world) },
          ...(mode === 'point' ? { adopt: 'area' as const } : {}),
        };
        return true;
      }

      // Typing: a press inside the box moves the caret, one outside ends it. How many
      // presses it is decides how much it takes — one the caret, two the word, three the
      // lot. `detail` is the browser's own count, so the timing that separates a double
      // click from two clicks is the one the rest of the machine uses.
      if (ctx.text?.current) {
        if (ctx.text.hits(world)) {
          if (ctx.clicks >= 3) ctx.text.selectAll();
          else if (ctx.clicks === 2) ctx.text.selectWordAt(world);
          else {
          // Only a single click starts a sweep. A double click sends a move of its own
          // between its two presses, and letting that extend from the word's start
          // collapsed the word straight back to a caret — you saw the selection flash
          // and vanish.
            ctx.text.pointTo(world, event.shiftKey);
            gesture = { kind: 'text' };
          }
          return true;
        }
        ctx.text.stop();
      }

      // A repeat's handles stand outside its frame, clear of the corners and their turn
      // ring, and are asked before both.
      const grip = repeatHandleAt(ctx, gripsOf(ctx), world);
      const pull = grip ? startPull(ctx, ctx.selection[0]!, grip.grip, world) : null;
      if (pull) {
        gesture = { kind: 'repeat', pull };
        hoverGrip = pull.grip;
        ctx.refresh();
        return true;
      }

      // The grips and the handles are small and sit on top of everything, so they are
      // asked first — the radius grips before the corners, since they sit inside them.
      if (ctx.selection.length === 1) {
        const grip = radiusGrips(ctx).find((item) => near(item.at, world, touchReach(RADIUS_REACH) * ctx.unitsPerPixel));
        const frame = grip ? frameOf(ctx, ctx.selection[0]!) : null;
        const node = frame ? (ctx.node(frame.id) as NodeRecord & { radii?: readonly number[] }) : null;
        if (grip && frame && node) {
          gesture = {
            kind: 'radius',
            id: frame.id,
            corner: grip.corner,
            box: frame.local,
            world: frame.world,
            before: [...(node.radii ?? [0, 0, 0, 0])] as [number, number, number, number],
            from: world,
            inset: insetOf(frame.local, frame.world, grip.corner, world),
            moving: false,
          };
          return true;
        }
      }

      // A line's ends are points to take, not the corners of a box with no height: taken
      // as a corner, an end could only slide along the line, never swing it round.
      if (ctx.selection.length === 1) {
        const end = lineEndAt(ctx, ctx.selection[0]!, world);
        if (end) {
          ctx.stopCapturing();
          gesture = { kind: 'line-end', ...end };
          return true;
        }
      }

      // Just outside a corner is where a shape is turned from, so it is asked before the
      // corner itself can claim the press — and before the canvas behind it. A line is
      // turned by its ends instead.
      // A board's name sits where the turn ring of its top left corner is, and the name is
      // what the press is aimed at: it takes the board rather than turning it.
      const onName = ctx.boardLabelAt?.(world) ?? null;
      if (ctx.selection.length > 0 && !loneLine(ctx) && !onName) {
        const first = selectionFrameOf(ctx);
        const pivot = pivotOf(ctx);
        const onHandle = handleAt(ctx, first, world);
        if (first && pivot && !onHandle && turnCorner(cornersOf(first), first.local, invert(first.world) ?? identity(), world, ctx.unitsPerPixel)) {
          const transforms = new Map<string, Matrix>();
          // A group and a layer inside it would each be turned, the layer twice; and one
          // locked layer would refuse the whole batch. Only what can move, once each.
          for (const id of movable(ctx, ctx.selection)) {
            const t = ctx.node(id)?.transform;
            if (t) transforms.set(id, [...t] as Matrix);
          }
          const [only] = transforms.size === 1 ? [...transforms.keys()] : [];
          const placed = only ? ctx.scene.worldTransform(only) : null;
          const angle = placed ? (Math.atan2(placed[1], placed[0]) * 180) / Math.PI : null;
          gesture = { kind: 'turn', pivot, from: Math.atan2(world.y - pivot.y, world.x - pivot.x), transforms, angle };
          return true;
        }
      }

      // The brackets sit on top of everything and are small, so they are asked first.
      if (ctx.selection.length === 1) {
        const which = bracketAt(ctx, ctx.selection[0]!, world);
        if (which) {
          gesture = { kind: 'ride', id: ctx.selection[0]!, which };
          return true;
        }
      }

      if (ctx.selection.length > 1) {
        const frame = selectionFrameOf(ctx);
        const handle = handleAt(ctx, frame, world);
        if (handle && frame) {
          const transforms = new Map<string, Matrix>();
          for (const id of movable(ctx, ctx.selection)) {
            const transform = ctx.node(id)?.transform;
            if (transform) transforms.set(id, [...transform] as Matrix);
          }
          // Artboards among them are resized, not stretched: each keeps its type at the
          // size it is and is given a new place and size, as one board on its own is.
          const boards = new Map<string, Rect>();
          for (const id of transforms.keys()) {
            const board = ctx.node(id) as (NodeRecord & { width?: number; height?: number }) | undefined;
            if (board?.type === 'artboard' && (ctx.scene.parentOf(id) ?? ROOT_ID) === ROOT_ID) {
              boards.set(id, { x: board.transform[4]!, y: board.transform[5]!, width: board.width ?? 0, height: board.height ?? 0 });
            }
          }
          gesture = { kind: 'selection-resize', box: frame.local, handle, fixed: handlesOf(frame)[OPPOSITE[handle]], transforms, grab: grabOf(handlesOf(frame)[handle], world), boards };
          return true;
        }
      }

      if (ctx.selection.length === 1) {
        const frame = frameOf(ctx, ctx.selection[0]!);
        const handle = handleAt(ctx, frame, world);
        if (handle && frame) {
          startResize(ctx, frame, handle, world);
          return true;
        }
      }

      // ⌘ (Ctrl elsewhere) reaches straight through groups to the layer drawn on top —
      // the way into a file of a thousand nested layers without a double click per level.
      const deep = event.metaKey || event.ctrlKey;
      // A board's name over its corner takes the board itself, which nothing else on the
      // canvas can: its empty middle is where a band starts (see `boardLabels.ts`).
      const named = onName;
      const hit = named ?? (deep ? (ctx.deepHit?.(world) ?? ctx.hit(world)) : ctx.hit(world));
      if (!hit) {
        // Empty canvas: drag a band round what you want rather than losing the
        // selection on the way to picking a new one.
        if (!event.shiftKey) ctx.setSelection([]);
        gesture = { kind: 'band', from: world, to: world };
        return true;
      }

      // A layer already chosen INSIDE the group that was hit, and under the pointer, is
      // what the press means: it drags on its own. The press used to take the whole group,
      // so a shape picked in Layers could not be moved by hand (QA 2026-09-24, layers-F4).
      const inner = event.shiftKey || named
        ? undefined
        : ctx.selection.find((id) => id !== hit && ctx.scene.isAncestor(hit, id) && drawsAt(ctx.scene, id, world, 4 * ctx.unitsPerPixel));
      const target = inner ?? hit;

      const selection = ctx.selection;
      // A press on the group that is already the one thing chosen goes INTO it, if the
      // hand lets go where it pressed: the layer under the pointer is chosen, as Figma
      // does. Decided on release, so dragging the group still moves the group.
      const enter =
        !event.shiftKey && !event.altKey && !inner && selection.length === 1 && selection[0] === target && ctx.node(target)?.type === 'group' ? target : null;
      // Shift on something already chosen means "not this one" only if the hand lets go
      // where it pressed; that is decided on release (QA 2026-09-24, sel-F21).
      const drop = event.shiftKey && selection.includes(target) ? target : null;
      if (event.shiftKey) {
        if (!drop) {
          ctx.setSelection([...selection, target]);
          // A choice built one shift-click at a time; at 5+ the hints offer Select Same Fill.
          emitHintSignal('selection.added', { size: selection.length + 1 });
        }
      } else if (!selection.includes(target)) {
        ctx.setSelection([target]);
      }

      // Dragging any of the chosen shapes moves all of them together.
      //
      // With option held it is copies that travel and the originals that stay — the
      // gesture every editor has, and the quickest way there is to lay out the same
      // thing several times. The copies land exactly on top, so the drag that follows
      // carries them off the originals and nothing appears to jump. They are made when
      // the press becomes a drag (see onPointerMove), never on the press itself.
      const moving = movable(ctx, ctx.selection.includes(target) ? ctx.selection : [target]);
      const origins = new Map<string, Point>();
      for (const id of moving) {
        const t = ctx.node(id)?.transform;
        if (t) origins.set(id, { x: t[4]!, y: t[5]! });
      }
      // The box as it was when the drag began: snapping has to measure from there,
      // because by the second move the shape has already been moved once.
      const box = unionOf(moving.map((id) => ctx.boundsOf(id)).filter((b): b is Rect => !!b));
      gesture = { kind: 'move', from: world, origins, box, moving: false, copy: event.altKey, drop, kept: null, enter };
      return true;
    },

    onPointerMove(event, world, ctx) {
      if (!gesture) {
        // Nothing pressed: say what the pointer is over. The cursor is the only sign the
        // turn ring is there at all — there is no knob to see any more — so it has to be
        // right the moment the hand arrives.
        const nextHandle = handleAt(ctx, selectionFrameOf(ctx), world);
        if (nextHandle !== hoverHandle) {
          hoverHandle = nextHandle;
          ctx.refresh();
        }
        const grip = repeatHandleAt(ctx, gripsOf(ctx), world)?.grip ?? null;
        if (grip !== hoverGrip) {
          hoverGrip = grip;
          ctx.refresh();
        }
        const frame = pointFrame(ctx);
        const edge = mode !== 'lasso' && frame ? pointHandleAt(ctx, frame, world) !== null : false;
        if (edge !== onFrameEdge) {
          onFrameEdge = edge;
          ctx.refresh();
        }
        const aiming = mode === 'object' && !hoverHandle && !hoverGrip && !edge && !ctx.points.active && !ctx.text?.current;
        const deep = event.metaKey || event.ctrlKey;
        const aimed = aiming ? (ctx.boardLabelAt?.(world) ?? (deep ? (ctx.deepHit?.(world) ?? ctx.hit(world)) : ctx.hit(world))) : null;
        const next = aimed && !ctx.selection.includes(aimed) ? aimed : null;
        // Over the group that is chosen, a click goes into it: what it would take is shown.
        const inside = aimed && !deep && ctx.selection.length === 1 && aimed === ctx.selection[0] ? layerInside(ctx, aimed, world) : null;
        if (next !== pointedAt || inside !== pointedInside) {
          pointedAt = next;
          pointedInside = inside;
          ctx.refresh();
        }
        const under = mode === 'object' ? null : pathUnder(ctx, world);
        if (under?.id !== hover?.id || under?.index !== hover?.index) {
          hover = under;
          ctx.refresh();
        }
        ctx.setCursor(cursorFor(ctx, world));
        if (ctx.points.active) ctx.points.hover(world);
        return;
      }
      if (event.buttons === 0) return;

      if (gesture.kind === 'repeat') {
        const props = pulled(gesture.pull, world);
        if (props) ctx.edit([{ op: 'set', id: gesture.pull.id, props }]);
        ctx.railChanged();
        ctx.refresh();
        return;
      }

      if (gesture.kind === 'points') {
        // A click on a point chooses it and leaves it where it was: a pixel of wobble used
        // to move it, snap it onto a guide and leave an undo step, as segment drags
        // already knew not to (QA 2026-09-25, select-points-F13).
        if (!gesture.moving && Math.hypot(world.x - gesture.from.x, world.y - gesture.from.y) < DRAG_SLOP * ctx.unitsPerPixel) return;
        gesture.moving = true;
        ctx.points.drag(world, event.altKey, event.metaKey);
        return;
      }
      if (gesture.kind === 'ride') {
        const ride = rideOf(ctx, gesture.id);
        const node = ctx.node(gesture.id);
        if (!ride || node?.type !== 'text' || !node.onPath) return;
        const where = ride.distance(world);
        const props =
          gesture.which === 'start'
            ? // The whole run slides: the span goes with it, so moving the type along the
              // curve does not quietly change how much of the curve it is allowed.
              { onPath: { ...node.onPath, start: where } }
            : { onPath: { ...node.onPath, span: Math.max(1, where - ride.start) } };
        ctx.edit([{ op: 'set', id: gesture.id, props }]);
        return;
      }
      if (gesture.kind === 'text') {
        ctx.text?.pointTo(world, true);
        return;
      }
      if (gesture.kind === 'band') {
        gesture.to = world;
        ctx.refresh();
        return;
      }
      if (gesture.kind === 'radius') {
        const { box, corner } = gesture;
        // Nothing changes until the press has travelled far enough to be a drag, and then
        // the radius grows by how far the hand has come in — from the radius it had, not
        // from the pointer's distance to the corner. The grip rests inside a square corner,
        // so the first twitch used to round all four corners at once (QA 2026-09-24, sel-N4).
        if (!gesture.moving && Math.hypot(world.x - gesture.from.x, world.y - gesture.from.y) < DRAG_SLOP * ctx.unitsPerPixel) return;
        gesture.moving = true;
        const index = CORNERS.indexOf(corner);
        const start = gesture.before[index] ?? 0;
        const came = insetOf(box, gesture.world, corner, world) - gesture.inset;
        const most = Math.min(box.width, box.height) / 2;
        const radius = Math.round(Math.min(Math.max(0, start + came), most));
        // One grip sets them all, which is what a corner radius nearly always means.
        // Command holds the others where they are, for the times it does not.
        const radii = (event.metaKey
          ? gesture.before.map((was, at) => (at === index ? radius : was))
          : [radius, radius, radius, radius]) as [number, number, number, number];
        ctx.edit([{ op: 'set', id: gesture.id, props: { radii } }]);
        return;
      }
      if (gesture.kind === 'segment' || gesture.kind === 'points-move') {
        const dx = world.x - gesture.from.x;
        const dy = world.y - gesture.from.y;
        // A click leaves the chosen points where they were; movement starts only after
        // the press has travelled far enough to be a drag.
        if (!gesture.moving && Math.hypot(dx, dy) < DRAG_SLOP * ctx.unitsPerPixel) return;
        if (!gesture.moving && gesture.kind === 'points-move') ctx.stopCapturing();
        gesture.moving = true;
        ctx.points.transformChosen(gesture.before, translation(dx, dy));
        return;
      }
      if (gesture.kind === 'points-resize') {
        const { fixed, from, handle, box, angle } = gesture;
        // The pointer is brought into the frame's own axes first, so a turned selection
        // grows along its own sides rather than along the page's.
        const pulled = applyToPoint(rotation(-angle), world.x, world.y);
        const across = handle.includes('w') || handle.includes('e');
        const down = handle.includes('n') || handle.includes('s');
        // Signed, so an edge pulled through the far side turns the points over rather than
        // stopping flat there; only nought itself is stepped round, which would lose them.
        const awayFromNought = (scale: number) => (Math.abs(scale) < 0.001 ? (scale < 0 ? -0.001 : 0.001) : scale);
        let sx = across ? awayFromNought(1 + (pulled.x - from.x) * (handle.includes('w') ? -1 : 1) / Math.max(1e-6, box.width)) : 1;
        let sy = down ? awayFromNought(1 + (pulled.y - from.y) * (handle.includes('n') ? -1 : 1) / Math.max(1e-6, box.height)) : 1;
        if (event.shiftKey) {
          const even = !down ? sx : !across ? sy : Math.max(sx, sy);
          sx = even;
          sy = even;
        }
        // A run of points can be flat — every anchor on one line — and then one of the
        // two scales is meaningless; leaving it at 1 keeps the drag working sideways
        // instead of collapsing the lot onto the fixed corner.
        if (box.width < 1e-6) sx = 1;
        if (box.height < 1e-6) sy = 1;
        // Scale in the frame's axes, then back out to the page: R · S · R⁻¹.
        const about = multiply(rotation(angle), multiply(scaleAbout(fixed, sx, sy), rotation(-angle)));
        ctx.points.transformChosen(gesture.before, about);
        return;
      }
      if (gesture.kind === 'points-turn') {
        const { pivot } = gesture;
        let degrees = ((Math.atan2(world.y - pivot.y, world.x - pivot.x) - gesture.from) * 180) / Math.PI;
        // The frame's own angle lands on a round one, as a shape's does (sel-F22).
        if (event.shiftKey) degrees = Math.round((gesture.was + degrees) / TURN_STEP) * TURN_STEP - gesture.was;
        ctx.points.transformChosen(gesture.before, rotateAbout(pivot, degrees));
        // The frame turns by exactly what the points did, so the box stays the same box
        // rather than being re-measured upright round a selection that has swung round.
        frameAngle = gesture.was + degrees;
        return;
      }
      if (gesture.kind === 'catch') {
        gesture.to = world;
        // A loop is only as fine as the hand drew it; samples nearer than a pixel say
        // nothing and would make the walk round its edges longer for no accuracy.
        const last = gesture.trail[gesture.trail.length - 1];
        if (gesture.shape === 'loop' && (!last || Math.hypot(world.x - last.x, world.y - last.y) >= ctx.unitsPerPixel)) {
          gesture.trail.push(world);
        }
        ctx.refresh();
        return;
      }
      if (gesture.kind === 'move') {
        let dx = world.x - gesture.from.x;
        let dy = world.y - gesture.from.y;
        // A hand moves a pixel or two while clicking; that is not a drag, and snapping
        // used to stretch it into a visible jump (QA 2026-09-24, sel-F14).
        if (!gesture.moving) {
          if (Math.hypot(dx, dy) < DRAG_SLOP * ctx.unitsPerPixel) return;
          gesture.moving = true;
          gesture.drop = null;
          liftIfBoards(ctx, gesture.origins);
          if (gesture.copy) {
            const kept = [...ctx.selection];
            const copies = ctx.duplicate(0);
            if (copies.length > 0) {
              // Someone who ⌥-drags copies knows the gesture: the hint that teaches it is done (lib/hints).
              emitHintSignal('layer.altCopied');
              gesture.kept = kept;
              gesture.origins = new Map();
              for (const id of copies) {
                const t = ctx.node(id)?.transform;
                if (t) gesture.origins.set(id, { x: t[4]!, y: t[5]! });
              }
            }
          }
        }
        /**
         * Option pressed part way through a drag copies what is being carried, there and
         * then, as it does in Illustrator and Figma: the originals go back to where they
         * started and the hand goes on with the copies. Only Option held at the press
         * copied, so a drag already under way could not become one (Farah, 2026-10-23).
         */
        else if (event.altKey && !gesture.copy && gesture.applied) {
          gesture.copy = true;
          const carried = new Map(gesture.origins);
          const kept = [...ctx.selection];
          const copies = ctx.duplicate(0);
          if (copies.length > 0) {
            emitHintSignal('layer.altCopied');
            const back = [];
            for (const [id, origin] of carried) {
              const t = ctx.node(id)?.transform;
              if (t) back.push({ op: 'set', id, props: { transform: [t[0], t[1], t[2], t[3], origin.x, origin.y] } });
            }
            if (back.length > 0) ctx.edit(back);
            gesture.kept = kept;
            gesture.origins = new Map();
            ctx.liftBoards?.(null);
            // The copies were made where the carried layers had got to: where they
            // start from is that, less how far the hand had carried them.
            for (const id of copies) {
              const t = ctx.node(id)?.transform;
              if (!t) continue;
              const along = deltaToParent(ctx.scene, id, gesture.applied.dx, gesture.applied.dy);
              gesture.origins.set(id, { x: t[4]! - along.dx, y: t[5]! - along.dy });
            }
            liftIfBoards(ctx, gesture.origins);
          }
        }
        // Shift keeps a drag on one axis, whichever it has gone further along.
        const level = event.shiftKey ? (Math.abs(dx) >= Math.abs(dy) ? 'y' : 'x') : null;
        if (level === 'y') dy = 0;
        else if (level === 'x') dx = 0;

        // Where the selection would land, and what it would be level with there.
        const moving = new Set(gesture.origins.keys());
        const here = gesture.box;
        /**
         * Command held says "leave it exactly where I put it".
         *
         * Control used to say it too, as a plain alias — and then control was given a
         * meaning of its own: show me the numbers. A key cannot both report a distance
         * and change the distance it is reporting. Holding it to read a gap and finding
         * the gap had stopped snapping shut is a tool arguing with itself, so the alias
         * is gone and command keeps the job on its own.
         *
         * Alt cannot be the key for it either: alt-drag pans the canvas, whatever tool
         * is in hand.
         */
        guides = [];
        ctx.catchGuides([]);
        if (here && !event.metaKey) {
          const landing: Rect = { ...here, x: here.x + dx, y: here.y + dy };
          const look = SNAP_LOOK * ctx.unitsPerPixel;
          const around: Rect = {
            x: landing.x - look,
            y: landing.y - look,
            width: landing.width + look * 2,
            height: landing.height + look * 2,
          };
          const targets: Rect[] = [];
          // Only worth gathering when something is going to read them: with smart guides
          // off this is a walk over everything on screen to build a list nobody uses.
          if (ctx.snap.smart) {
            // Nothing lines itself up with a shape it is carrying, with itself, or with
            // the group it sits in (view-snapping-F3).
            for (const id of ctx.snapNeighbours(around, [...moving])) {
              const box = ctx.boundsOf(id);
              if (box) targets.push(box);
            }
          }
          const snap = snapDrag(landing, { targets, guides: ctx.guides }, ctx.snap, SNAP_REACH * ctx.unitsPerPixel);
          // Snapping never pulls a shift-held drag off its axis.
          if (level !== 'x') dx += snap.dx;
          if (level !== 'y') dy += snap.dy;
          guides = level ? snap.lines.filter((line) => line.axis !== level) : snap.lines;
          ctx.catchGuides(snap.onGuides ?? []);
        }

        const ops = [];
        for (const [id, origin] of gesture.origins) {
          const t = ctx.node(id)?.transform;
          if (!t) continue;
          // The drag is measured on the page; the layer moves in its parent's axes.
          const along = deltaToParent(ctx.scene, id, dx, dy);
          ops.push({ op: 'set', id, props: { transform: [t[0], t[1], t[2], t[3], origin.x + along.dx, origin.y + along.dy] } });
        }
        if (ops.length > 0) ctx.edit(ops);
        gesture.applied = { dx, dy };
        ctx.refresh();
        return;
      }

      if (gesture.kind === 'line-end') {
        const { id, fixed } = gesture;
        const landed = event.metaKey ? world : ctx.place(world, [id]);
        let dx = landed.x - fixed.x;
        let dy = landed.y - fixed.y;
        // Shift holds it level, upright or diagonal, as it does while a line is drawn.
        if (event.shiftKey) {
          const angle = Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * (Math.PI / 4);
          const reach = dx * Math.cos(angle) + dy * Math.sin(angle);
          dx = Math.abs(Math.cos(angle)) < 1e-12 ? 0 : reach * Math.cos(angle);
          dy = Math.abs(Math.sin(angle)) < 1e-12 ? 0 : reach * Math.sin(angle);
        }
        // Measured in the line's parent, where its transform lives.
        const parent = ctx.scene.parentOf(id);
        const outer = parent ? ctx.scene.worldTransform(parent) : identity();
        const back = invert(outer);
        if (!back) return;
        const from = applyToPoint(back, fixed.x, fixed.y);
        const to = applyToPoint(back, fixed.x + dx, fixed.y + dy);
        const length = Math.max(1e-3, Math.hypot(to.x - from.x, to.y - from.y));
        const angle = Math.atan2(to.y - from.y, to.x - from.x);
        const cos = Math.abs(Math.cos(angle)) < 1e-12 ? 0 : Math.cos(angle);
        const sin = Math.abs(Math.sin(angle)) < 1e-12 ? 0 : Math.sin(angle);
        // The line runs from its origin: the kept end is the origin and the hand the far end,
        // which is the same line whichever end was taken.
        ctx.edit([{ op: 'set', id, props: { length, transform: [cos, sin, -sin, cos, from.x, from.y] } }]);
        ctx.refresh();
        return;
      }

      if (gesture.kind === 'selection-resize') {
        const { box, handle, fixed, transforms, grab } = gesture;
        const landed = handlePlace(ctx, world, grab, handle, [...transforms.keys()], false, event.metaKey);
        const { width, height, flipX, flipY } = resizedBox(box, handle, fixed, landed, event.shiftKey);
        // Past the fixed side, the layers turn over about it.
        const sx = box.width < 1e-6 ? 1 : (flipX ? -1 : 1) * width / box.width;
        const sy = box.height < 1e-6 ? 1 : (flipY ? -1 : 1) * height / box.height;
        const scale = scaleAbout(fixed, sx, sy);
        const ops = [];
        for (const [id, before] of transforms) {
          const board = gesture.boards?.get(id);
          if (board && ctx.node(id)) {
            const a = applyToPoint(scale, board.x, board.y);
            const b = applyToPoint(scale, board.x + board.width, board.y + board.height);
            const turned = before[0] !== 1 || before[1] !== 0 || before[2] !== 0 || before[3] !== 1;
            if (!turned) {
              ops.push({ op: 'set', id, props: {
                transform: [1, 0, 0, 1, Math.min(a.x, b.x), Math.min(a.y, b.y)],
                width: Math.max(1, Math.abs(b.x - a.x)),
                height: Math.max(1, Math.abs(b.y - a.y)),
              } });
              continue;
            }
          }
          const parent = ctx.scene.parentOf(id);
          const outer = parent ? ctx.scene.worldTransform(parent) : identity();
          const back = invert(outer);
          if (!back || !ctx.node(id)) continue;
          // Keep the scale on the page even when the layers have different, rotated parents.
          ops.push({ op: 'set', id, props: { transform: multiply(multiply(back, scale), multiply(outer, before)) } });
        }
        if (ops.length > 0) ctx.edit(ops);
        return;
      }

      if (gesture.kind === 'turn') {
        const { pivot, transforms, angle } = gesture;
        let degrees = ((Math.atan2(world.y - pivot.y, world.x - pivot.x) - gesture.from) * 180) / Math.PI;
        // Shift lands one shape on a round angle, not on 15° steps from wherever it began
        // — a shape at 10° could never be put back upright (QA 2026-09-24, sel-F22).
        // Several shapes still turn in steps from the start, as they share no one angle.
        if (event.shiftKey) {
          degrees =
            angle === null
              ? Math.round(degrees / TURN_STEP) * TURN_STEP
              : Math.round((angle + degrees) / TURN_STEP) * TURN_STEP - angle;
        }
        // Turning happens on the page, around the pivot; what is stored is the node's
        // transform in its parent, so the turn is carried into the parent and back out.
        const turn = rotateAbout(pivot, degrees);
        const ops = [];
        for (const [id, before] of transforms) {
          const parent = ctx.scene.parentOf(id);
          const outer = parent ? ctx.scene.worldTransform(parent) : identity();
          const back = invert(outer);
          if (!back) continue;
          ops.push({ op: 'set', id, props: { transform: multiply(multiply(back, turn), multiply(outer, before)) } });
        }
        if (ops.length > 0) ctx.edit(ops);
        return;
      }

      if (gesture.kind === 'picture-move') {
        const { frame, picture, from } = gesture;
        const back = invert(frame.world);
        if (!back) return;
        const here = applyToPoint(back, world.x, world.y);
        const { placed } = picture;
        const box = frame.local;
        // It can only stop at the frame's edge if the edge is there to stop at: ⌘ lets it go past.
        const free = event.metaKey || event.ctrlKey;
        const x = free ? placed.x + here.x - from.x : heldInFrame(placed.x + here.x - from.x, placed.x, placed.width, box.x, box.width);
        const y = free ? placed.y + here.y - from.y : heldInFrame(placed.y + here.y - from.y, placed.y, placed.height, box.y, box.height);
        ctx.edit([{ op: 'set', id: frame.id, props: picture.showing({ x: box.x - (x - placed.x), y: box.y - (y - placed.y), width: box.width, height: box.height }) }]);
        return;
      }

      // Resizing: measured in the shape's own coordinates, so a shape that has been
      // turned still grows along its own sides rather than the screen's.
      const { frame, handle, fixed } = gesture;
      const node = ctx.node(frame.id);
      const back = invert(frame.world);
      if (!node || !back) return;
      /**
       * The corner lands where a corner should, and THEN the size is read from it.
       *
       * Snapped on the page rather than in the shape's own axes, because that is where
       * the guides and the other shapes are — an edge pulled onto a guide has to meet it
       * on the page, not at whatever angle this shape happens to be lying at. Command
       * still says "leave it exactly where I put it", as it does for a move.
       *
       * On a picture ⌘ means crop instead: the handle moves and the picture does not, so the
       * frame is the only thing being placed, and it snaps like any other frame.
       */
      const crop = !!gesture.picture && (cropping === frame.id || event.metaKey || event.ctrlKey);
      const landed = handlePlace(ctx, world, gesture.grab, handle, [frame.id], Math.abs(frame.world[1]!) > 1e-6 || Math.abs(frame.world[2]!) > 1e-6, event.metaKey && !gesture.picture);
      const pointer = applyToPoint(back, landed.x, landed.y);
      const { x, y, width, height, flipX, flipY } = resizedBox(frame.local, handle, fixed, pointer, event.shiftKey);

      const sized = sizeOf(node);
      if (sized.width > 0 || sized.height > 0 || node.type === 'text') {
        // A shape with a width and a height is given the new ones, so its stroke stays
        // the weight it was and its corners the radius they were.
        const props: Record<string, unknown> = {
          width,
          ...(node.type === 'line' ? {} : { height }),
          transform: multiply(gesture.transform, translation(x - frame.local.x, y - frame.local.y)),
        };
        if (node.type === 'line') {
          // A line has a length, not a width: writing `width` was refused as an unknown
          // field and the whole drag did nothing (QA 2026-09-24, sel-F3).
          props.length = props.width;
          delete props.width;
          delete props.height;
        }
        if (node.type === 'text') {
          // Shift means the type grows with the box rather than the box growing around
          // it. Shift is already "keep the proportions", so the scale is ONE number, and
          // that is exactly what makes scaling type well defined — there is no honest way
          // to answer a box pulled wider than it is tall, since a glyph stretched on one
          // axis is a different typeface, not a bigger one.
          if (event.shiftKey && gesture.fontSize > 0) {
            const factor = width / Math.max(1e-6, frame.local.width);
            props.style = { ...(node.style ?? {}), fontSize: Math.max(1, gesture.fontSize * factor) };
          } else if (node.sizing !== 'fixed') {
            // A box given a size on its own keeps it, rather than snapping back to its text.
            props.sizing = 'fixed';
          }
        }
        // Worked out from the press every time, so ⌘ can come and go during the drag.
        if (crop && gesture.picture) Object.assign(props, gesture.picture.showing({ x, y, width, height }));
        else if (gesture.cropped && gesture.picture) Object.assign(props, gesture.picture.was);
        gesture.cropped = crop;
        const ops: unknown[] = [{ op: 'set', id: frame.id, props }];
        // An artboard's layers are drawn from its origin, which a top or left corner moves:
        // each is moved back by as much, so the art stays where it is on the page, as the
        // Artboard tool does (QA 2026-09-24, sel-F19). force, as there: a locked layer must
        // not refuse the batch and stop the board resizing.
        const shiftX = x - frame.local.x;
        const shiftY = y - frame.local.y;
        if (shiftX !== 0 || shiftY !== 0) {
          for (const kid of gesture.kids) {
            const [a, b, c, d, e, f] = kid.transform;
            ops.push({ op: 'set', id: kid.id, props: { transform: [a, b, c, d, e - shiftX, f - shiftY] }, force: true });
          }
        }
        ctx.edit(ops);
        return;
      }

      // A path or a group has no width to set, so the box is scaled instead: the fixed
      // corner stays put and everything inside grows away from it.
      // A perfectly straight path has no size across itself, and dividing by that gave a
      // scale past the schema's limit, so every drag was refused. That axis keeps its
      // scale, as the points frame already does (QA 2026-09-24, sel-F13).
      const sx = frame.local.width < 1e-6 ? 1 : (flipX ? -1 : 1) * width / frame.local.width;
      const sy = frame.local.height < 1e-6 ? 1 : (flipY ? -1 : 1) * height / frame.local.height;
      ctx.edit([{ op: 'set', id: frame.id, props: { transform: multiply(gesture.transform, scaleAbout(fixed, sx, sy)) } }]);
    },

    onPointerUp(event, _world, ctx) {
      const shiftHeld = event.shiftKey;
      // Put down whatever boards the drag carried, before anything asks where they are.
      ctx.liftBoards?.(null);
      guides = [];
      ctx.catchGuides([]);
      if (!gesture) return;
      if (gesture.kind === 'band') {
        const band = bandOf(gesture.from, gesture.to);
        // A band that never moved is just a click on nothing.
        if (band.width > 2 * ctx.unitsPerPixel || band.height > 2 * ctx.unitsPerPixel) {
          // What a hand could click, and a group rather than the group AND its insides:
          // a box used to catch hidden layers, the contents of locked groups, and groups
          // together with their children — so Delete removed protected work and a move
          // carried every child twice, tearing the group apart.
          const candidates = ctx.selectionScope ? ctx.scene.childrenOf(ctx.selectionScope) : ctx.scene.paintOrder();
          // Begun on bare canvas, off every artboard, a band takes a board whole only when
          // it closes round all of it, as Figma does — the way to choose several at once.
          // A board it merely crosses gives up the layers the band touches instead, so a
          // box dragged in from the side over some folders catches the folders (and the
          // picture behind them) rather than the board they sit on.
          // Begun on a board, it is reaching for the layers on it, as it always has.
          const boards = !ctx.selectionScope && !ctx.artboardAt(gesture.from);
          const caught = candidates.filter((id) => {
            const box = ctx.boundsOf(id);
            if (!box || !intersects(box, band)) return false;
            if (boards && boardOnPage(ctx.scene, id)) return encloses(band, box);
            return ctx.scene.choosable(id);
          });
          ctx.setSelection(ctx.scene.topmost([...new Set([...ctx.selection, ...caught])]));
        }
      } else if (gesture.kind === 'catch') {
        const catching = gesture;
        // A lasso normally returns to its starting point. Its travelled extent, not
        // just the release distance, distinguishes a loop from a click.
        const moved = catching.shape === 'loop'
          ? catching.trail.reduce((extent, point) => Math.max(extent, Math.hypot(point.x - catching.from.x, point.y - catching.from.y)), 0)
          : Math.hypot(catching.to.x - catching.from.x, catching.to.y - catching.from.y);
        if (moved > CATCH_SLOP * ctx.unitsPerPixel) {
          const caught = caughtBy(ctx, regionOf(catching));
          if (caught) {
            // A region that lands on a different path moves the work there, which is how
            // one gesture can reach points on a shape that was not open a moment ago.
            if (ctx.points.editingId !== caught.id) {
              ctx.setSelection([caught.id]);
              ctx.points.open(caught.id);
            }
            ctx.points.selectIndices(caught.indices, shiftHeld);
          } else if (!shiftHeld) {
            ctx.points.selectIndices([], false);
          }
          // Dragging out a region from the point cursor is the box cursor's gesture, so
          // that is the cursor it leaves in hand — the rail follows what you just did.
          if (catching.adopt) mode = catching.adopt;
          // A region makes the selection afresh, so its frame starts upright.
          frameAngle = 0;
          // The rail's lower well counts the chosen anchors, and choosing them here
          // touches neither the tool's settings nor the selection — the two things the
          // editor watches — so without this the buttons stay a gesture behind.
          ctx.railChanged();
        } else if (catching.shape === 'loop' && catching.tap) {
          // A tap may choose a point, but a miss only clears the point selection.
          // Keep Lasso and the open path ready for the next loop.
          const under = pathUnder(ctx, catching.from);
          if (under && under.id !== ctx.points.editingId) {
            ctx.setSelection([under.id]);
            ctx.points.open(under.id);
          }
          if (!ctx.points.press(catching.from, shiftHeld) && !shiftHeld) ctx.points.selectIndices([], false);
          ctx.points.release();
          frameAngle = 0;
          ctx.railChanged();
        } else if (catching.tap) {
          // Never became a drag, so the press meant what a click means.
          const { hit } = catching.tap;
          if (hit && ctx.node(hit)?.type === 'path') {
            ctx.setSelection([hit]);
            ctx.points.open(hit);
          } else if (hit) {
            // Not a path: choose it as a shape rather than doing nothing at all.
            ctx.points.close();
            ctx.setSelection(shiftHeld && !ctx.selection.includes(hit) ? [...ctx.selection, hit] : [hit]);
          } else {
            ctx.points.close();
            ctx.setSelection([]);
          }
          frameAngle = 0;
          ctx.railChanged();
        }
      } else if (
        gesture.kind === 'points-resize' ||
        gesture.kind === 'points-turn' ||
        gesture.kind === 'radius'
      ) {
        ctx.stopCapturing();
      } else if (gesture.kind === 'segment' || gesture.kind === 'points-move') {
        if (gesture.kind === 'segment' || gesture.moving) ctx.stopCapturing();
        ctx.railChanged();
      } else if (gesture.kind === 'points') {
        ctx.points.release();
        ctx.stopCapturing();
        ctx.railChanged();
      } else if (gesture.kind === 'move') {
        // A shift-click that never became a drag: now it lets go of the layer (sel-F21).
        const { drop } = gesture;
        if (!gesture.moving && drop) ctx.setSelection(ctx.selection.filter((id) => id !== drop));
        if (!gesture.moving && gesture.enter && ctx.enterGroup?.(gesture.enter, _world)) {
          // The double-click this click may be the second half of has been answered: it
          // does not go a second level down and open the shape's points as well.
          enteredByClick = true;
        }
        // Dropped on an artboard, it belongs to that artboard; dropped off every one, it
        // belongs to the page. Without this a layer dragged onto an artboard was drawn on
        // it and yet not in it — skipped by its animation, its export and its layer list.
        const moved = [...gesture.origins].some(([id, from]) => {
          const t = ctx.node(id)?.transform;
          return t !== undefined && (t[4] !== from.x || t[5] !== from.y);
        });
        if (moved) ctx.settleIntoArtboards([...gesture.origins.keys()]);
        ctx.stopCapturing();
      } else if (gesture.kind !== 'text') {
        ctx.stopCapturing();
      }
      // Let go off the handle: it no longer grows or speaks until the hand comes back.
      if (gesture.kind === 'repeat') {
        hoverGrip = null;
        ctx.refresh();
      }
      gesture = null;
    },

    cropPicture(id, ctx) {
      setCropping(ctx, id && framedPicture(ctx, id, frameOf(ctx, id)?.local ?? { x: 0, y: 0, width: 0, height: 0 }) ? id : null);
    },

    onDoubleClick(_event, world, ctx) {
      if (cropping) {
        setCropping(ctx, null);
        return true;
      }
      // A visible anchor owns the gesture, even inside a group or outside its fill.
      // Work on this anchor alone; the toolbar remains the way to convert a whole set.
      const anchor = ctx.points.active ? ctx.points.hit(world) : null;
      if (anchor?.kind === 'anchor') {
        ctx.stopCapturing();
        ctx.points.selectIndices([anchor.index], false);
        ctx.points.convertAt(anchor.index);
        ctx.stopCapturing();
        addingPoints = false;
        if (mode === 'object') mode = 'point';
        frameAngle = 0;
        ctx.railChanged();
        ctx.refresh();
        return true;
      }
      if (enteredByClick) {
        enteredByClick = false;
        return true;
      }
      // Enter the group before looking for path anchors inside it.
      const hit = ctx.hit(world);
      const node = hit ? ctx.node(hit) : undefined;
      if (hit && node?.type === 'group') return ctx.enterGroup?.(hit, world) ?? false;
      // A text cut-out: the picture showing through the letters lies over the text that
      // cuts it, so a click on the letters takes the picture. A second click there means
      // the words, though — the picture has nothing to open — so it types into the mask.
      const mask = hit && ctx.text ? maskTextUnder(ctx, hit, world) : null;
      if (mask && ctx.text) {
        if (ctx.text.editingId === mask) return true;
        ctx.setSelection([mask]);
        ctx.text.startAt(mask, world);
        return true;
      }
      // A picture has nothing to open but itself: a second click puts the hand on it.
      const frame = hit ? frameOf(ctx, hit) : null;
      if (hit && frame && framedPicture(ctx, hit, frame.local)) {
        ctx.setSelection([hit]);
        setCropping(ctx, hit);
        return true;
      }
      // Outside point editing, a double click opens the anchor without changing its shape.
      const near = pathUnder(ctx, world);
      if (near && near.index !== null) {
        if (ctx.points.editingId !== near.id) {
          ctx.setSelection([near.id]);
          ctx.points.open(near.id);
        }
        ctx.points.selectIndices([near.index], false);
        if (mode === 'object') mode = 'point';
        frameAngle = 0;
        ctx.showFields();
        ctx.railChanged();
        return true;
      }

      if (!hit) return false;
      // A second click opens what is inside: a path's points, or text for typing.
      if (node?.type === 'path') {
        // A second click is a request to work inside the shape, so the cursor becomes the
        // one that can: opening the points and leaving the shape cursor in hand would
        // mean the very next press closed them again.
        ctx.setSelection([hit]);
        ctx.points.open(hit);
        frameAngle = 0;
        if (mode === 'object') mode = 'point';
        ctx.railChanged();
        return true;
      }
      if (node?.type === 'text' && ctx.text) {
        // Already typing in this one: the press that came with this double click has
        // taken the word under it, and re-opening the box here would put the caret back
        // and throw that selection away.
        if (ctx.text.editingId === hit) return true;
        ctx.setSelection([hit]);
        ctx.text.startAt(hit, world);
        return true;
      }
      return false;
    },

    draw(overlay, ctx) {
      const held = croppedPicture(ctx);
      if (cropping && !held) setCropping(ctx, null);
      if (held) {
        const { frame, picture } = held;
        const corners = cornersOf(frame);
        // Where it lies now, which the drag has been writing, so the fade moves with the hand.
        overlay.picture?.(picture.assetId, frame.world, picture.placed, frame.local, 0.35);
        const box = picture.placed;
        const whole = [[0, 0], [1, 0], [1, 1], [0, 1], [0, 0]].map(([u, v]) => ({ at: applyToPoint(frame.world, box.x + u! * box.width, box.y + v! * box.height) }));
        overlay.curve(whole, { width: 1 });
        overlay.curve([corners.nw, corners.ne, corners.se, corners.sw, corners.nw].map((at) => ({ at })));
        for (const key of CORNERS) overlay.handle(corners[key], { hovered: hoverHandle === key });
        return;
      }
      // While a path's points are open, its box and corner handles only get in the way.
      if (!ctx.points.active) {
        for (const id of ctx.selection) {
          // Text riding a curve is shown by lighting the curve, not by an upright box:
          // its box is a flat one somewhere off to the side, and drawing that where the
          // words are not says the wrong thing about what is chosen. There is nothing to
          // resize either — the type follows the path, so the path is the handle.
          const riding = ctx.node(id);
          if (riding?.type === 'text' && riding.onPath) {
            const ride = rideOf(ctx, id);
            if (ride) {
              const world = ctx.scene.worldTransform(id);
              overlay.curve(
                ride.ruler.points.map((point) => ({ at: applyToPoint(world, point.x, point.y) })),
                { width: 1 },
              );
              // A bracket at each end of the run: where the words begin, and how far
              // along they may go. Drawn standing off the curve rather than on it, so
              // they can be grabbed without fighting the letters sitting there.
              for (const which of ['start', 'end'] as const) {
                const distance = which === 'start' ? ride.start : ride.start + ride.span;
                const foot = ride.at(distance);
                const lean = atLength(ride.ruler, distance);
                if (!foot || !lean) continue;
                const radians = (lean.angle * Math.PI) / 180;
                // Across the curve rather than off one side of it: a bracket that
                // straddles the line reads as a mark ON the path, which is what it is,
                // and stays findable whichever way the curve has turned.
                const nx = Math.sin(radians);
                const ny = -Math.cos(radians);
                const above = { x: foot.x + nx * ride.size, y: foot.y + ny * ride.size };
                const below = { x: foot.x - nx * ride.size * 0.35, y: foot.y - ny * ride.size * 0.35 };
                overlay.line(above, below, { width: 1 });
                overlay.handle(foot);
              }
              continue;
            }
          }
          const frame = frameOf(ctx, id);
          if (!frame) continue;
          const corners = cornersOf(frame);
          // The outline follows the shape, so a turned shape is framed on the slant
          // rather than by the upright box that happens to contain it.
          overlay.curve([corners.nw, corners.ne, corners.se, corners.sw, corners.nw].map((at) => ({ at })));
          if (ctx.selection.length !== 1) continue;
          // A line's two ends, which are what it is pulled by.
          if (loneLine(ctx) === id) {
            const placed = ctx.scene.worldTransform(id);
            const length = (ctx.node(id) as NodeRecord & { length?: number }).length ?? 0;
            overlay.handle(applyToPoint(placed, 0, 0));
            overlay.handle(applyToPoint(placed, length, 0));
            continue;
          }
          // Hollow, like every selection frame worth copying: a solid square is what an
          // anchor uses to say it is chosen, and a resize handle is never chosen — it is
          // somewhere to pull. Only the one under the pointer grows.
          for (const key of CORNERS) {
            overlay.handle(corners[key], { hovered: hoverHandle === key });
          }
        }
        // Several chosen get the same visible frame and resize handles as one layer.
        if (ctx.selection.length > 1) {
          const shared = selectionFrameOf(ctx);
          if (shared) {
            const corners = cornersOf(shared);
            overlay.curve([corners.nw, corners.ne, corners.se, corners.sw, corners.nw].map((at) => ({ at })));
            for (const key of CORNERS) overlay.handle(corners[key], { hovered: hoverHandle === key });
          }
        }
      }

      // A repeat's handles, while nothing else is being done to it. The one under the hand
      // or being pulled says what it sets, in the popover's words, as it changes.
      if (!gesture || gesture.kind === 'repeat') {
        const held = gesture?.kind === 'repeat' ? gesture.pull.grip : hoverGrip;
        for (const handle of gripsOf(ctx)) {
          const on = handle.grip === held;
          overlay.grip(handle.at, { toward: handle.toward, both: handle.both, hovered: on });
          if (!on) continue;
          const id = ctx.selection[0]!;
          const { x, y } = handle.toward;
          overlay.label(handle.at, gripReadout(ctx.node(id), handle.grip, sourceOf(ctx, id)), {
            align: x > 0.38 ? 'left' : x < -0.38 ? 'right' : 'center',
            baseline: y > 0.38 ? 'top' : y < -0.38 ? 'bottom' : 'middle',
            offset: { x: x * 16, y: y * 16 },
          });
        }
      }

      // The radius grips, inside the corners of the one shape that has them.
      for (const grip of radiusGrips(ctx)) {
        overlay.knob(grip.at, { hovered: gesture?.kind === 'radius' && gesture.corner === grip.corner });
      }

      // The frame round chosen anchors, drawn like a shape's because it is worked like one
      // — but at half the weight, since here it is the second thing on the canvas and the
      // anchors inside it are the first.
      const frame = pointFrame(ctx);
      if (frame && gesture?.kind !== 'catch') {
        const { nw, ne, se, sw } = frame.corners;
        const held = gesture?.kind === 'points-resize' || gesture?.kind === 'points-turn' || gesture?.kind === 'points-move';
        overlay.curve([nw, ne, se, sw, nw].map((at) => ({ at })), { width: mode !== 'lasso' && (onFrameEdge || held) ? 1.5 : 1 });
        if (mode !== 'lasso') for (const corner of [nw, ne, sw, se]) overlay.handle(corner, { size: 3 });
      }

      if (gesture?.kind === 'band') {
        overlay.box(bandOf(gesture.from, gesture.to), { fill: true, alpha: 0.12 });
      }

      if (gesture?.kind === 'catch') {
        if (gesture.shape === 'box') overlay.box(bandOf(gesture.from, gesture.to), { fill: true, alpha: 0.12 });
        else if (gesture.trail.length > 1) {
          // Drawn back to where it started, because that is the shape being asked about:
          // the loop closes itself whether or not the hand came round.
          overlay.curve([...gesture.trail, gesture.trail[0]!].map((at) => ({ at })), { width: 1 });
        }
      }

      // A path's own guides, drawn the same way a shape's are: the line says what the
      // anchor agreed with, not merely that it stopped.
      for (const line of ctx.points.guides) {
        if (line.axis === 'x') overlay.guide({ x: line.at, y: line.from }, { x: line.at, y: line.to });
        else overlay.guide({ x: line.from, y: line.at }, { x: line.to, y: line.at });
      }

      for (const line of guides) {
        if (line.axis === 'x') overlay.guide({ x: line.at, y: line.from }, { x: line.at, y: line.to });
        else overlay.guide({ x: line.from, y: line.at }, { x: line.to, y: line.at });
      }

      // The point on offer belongs to the hover, so it goes the moment a press turns into
      // a drag: leaving it where the hand found it makes it look pinned while the path it
      // was offered on moves out from under it.
      // The shape under the pointer, traced, so it is clear which one a press would reach
      // into. The open one already shows its anchors and needs no second outline.
      if (hover && !gesture && hover.id !== ctx.points.editingId) {
        for (const outline of ctx.points.outlineOf(hover.id) ?? []) overlay.curve(outline.map((at) => ({ at })), { width: 1 });
        // And the anchor itself, when the hand is near one: what you can see is what the
        // press will take, without having to open the shape to find out.
        if (hover.index !== null) {
          const at = (ctx.points.anchorsOf(hover.id) ?? []).find((anchor) => anchor.index === hover!.index);
          if (at) overlay.handle(at.at, { hovered: true });
        }
      }

      const outlined = pointedAt ?? pointedInside;
      if (outlined && !gesture && ctx.node(outlined)) {
        const frame = frameOf(ctx, outlined);
        if (frame) {
          const c = cornersOf(frame);
          for (const [from, to] of [[c.nw, c.ne], [c.ne, c.se], [c.se, c.sw], [c.sw, c.nw]] as const) overlay.line(from, to, { width: 1, alpha: 0.75 });
        }
      }
      drawPoints(overlay, ctx, mode !== 'object' && mode !== 'lasso' && !gesture, addingPoints);
      if (previewEnds && !gesture) drawEndConnection(overlay, ctx);
      // Last, so the numbers sit over everything they are about.
      drawMeasures(overlay, ctx);
    },

    deactivate(ctx) {
      previewEnds = false;
      addingPoints = false;
      // A drag cut short by a tool change or a second finger still ends properly (sel-F23).
      interrupt(ctx);
      setCropping(ctx, null);
      hoverHandle = null;
      hoverGrip = null;
      guides = [];
      ctx.catchGuides([]);
      frameAngle = 0;
      onFrameEdge = false;
      hover = null;
      pointedAt = null;
      pointedInside = null;
      enteredByClick = false;
      // Another tool closes the points, so the point cursor goes with them: picked up
      // again, Select starts where it always starts.
      mode = 'object';
      // A point still held (a press the system took away) lets go too, so Delete and undo
      // are not refused afterwards for a drag nobody is making (select-points-F20).
      ctx.points.release();
      ctx.setCursor(null);
    },

    /**
     * No path has its points open any more: back to choosing whole shapes, as Done does.
     * The point cursors are for working inside a path, and there is no way into them but
     * by opening one, so with none open they are a cursor with nothing to point at. Said
     * for every way the points close — Escape, Return, a click on nothing or on a shape
     * that is not a path, a layer picked in the list, the path deleted. It used to be said
     * for the keys alone, so a click off the shape left the Edit Points strip up, the quick
     * bar away and the next drag drawing a box (QA 2026-09-25, select-points-F9).
     */
    onPointsClosed(ctx) {
      previewEnds = false;
      addingPoints = false;
      mode = 'object';
      frameAngle = 0;
      hover = null;
      ctx.refresh();
    },

    /**
     * Select's rail: how to point, then what to do with what was pointed at.
     *
     * This is the rail the editor wears most — it is the tool you start in and the one
     * every other tool hands back to — so it has to earn its four slots. The top well is
     * the four ways of pointing, which is the choice that changes what every press means.
     * The bottom well is what can be done with the result, and that follows the mode: in
     * object mode you have shapes and can lock, hide or delete them; in a point mode you
     * have anchors and can take them out or join the path's ends.
     *
     * A button that has nothing to act on is shown and disabled rather than hidden. The
     * well would otherwise change height as you worked, and a rail that moves under the
     * thumb is worse than one with a grey button in it.
     */
    rail(ctx: ToolContext): Rail {
      const modes: { id: Mode; icon: 'select-object' | 'select-point' | 'select-area' | 'select-lasso'; title: string }[] = [
        { id: 'object', icon: 'select-object', title: 'Object — select whole shapes' },
        { id: 'point', icon: 'select-point', title: 'Direct — select the points inside a shape' },
        { id: 'area', icon: 'select-area', title: 'Marquee — drag a box round points' },
        { id: 'lasso', icon: 'select-lasso', title: 'Lasso — draw a loop round points' },
      ];

      const top = {
        kind: 'buttons' as const,
        items: modes.map((item) => ({
          id: item.id,
          icon: item.icon,
          title: item.title,
          pressed: mode === item.id,
          run: () => {
            mode = item.id;
            addingPoints = false;
            // Done always leaves point editing. Transforming points needs no mode switch.
            if (mode === 'object') { ctx.points.close(); previewEnds = false; }
            if (mode !== 'object') {
              openChosenPath(ctx);
              frameAngle = 0;
            }
            ctx.railChanged();
            ctx.refresh();
          },
        })),
      };

      /**
       * What can be reached from what is chosen: the lower well, in every mode.
       *
       * These replace lock, hide and delete, which were the wrong things to give four of
       * the rail's twelve slots to — every one of them is a keystroke away and all three
       * are in the layers list. What is NOT anywhere else is the work of getting the right
       * things chosen in the first place, which on a drawing of any size is most of the
       * work there is.
       */
      const reaching = (): Well => {
        // The wording follows the CURSOR, not whether a path happens to be open: a button
        // that describes shapes while the point cursor is in hand is a button lying about
        // what it is going to do.
        const points = mode !== 'object';
        const chosen = points ? ctx.points.chosenCount > 0 : ctx.selection.length > 0;
        const items: RailButton[] = [
          {
            id: 'grow',
            icon: 'plus',
            label: 'Grow selection',
            title: points
              ? 'Take in the anchors either side — ⌘ for the whole path'
              : 'Take in what is beside it — ⌘ for the whole cluster',
            disabled: !chosen,
            run: ({ more }) => reach(ctx, more ? 'whole' : 'grow'),
          },
          {
            id: 'shrink',
            icon: 'minus',
            label: 'Shrink selection',
            title: points ? 'Drop the ends of each run' : 'Drop whatever is on the edge of it',
            disabled: !chosen,
            run: () => reach(ctx, 'shrink'),
          },
        ];

        if (mode === 'object') {
          items.push({
            id: 'same',
            icon: 'eyedropper',
            title: 'Take everything filled the same — ⌘ for everything the same size',
            disabled: !chosen,
            run: ({ more }) => { emitHintSignal('selection.same'); sameAs(ctx, more ? 'size' : 'fill'); },
          });
        } else {
          // Symmetry belongs to the point cursors, and is shown there whether or not a
          // path is open — a well that changes height as you work is a well nobody can
          // aim at. It is live only where an axis was actually found: a toggle that
          // silently does nothing on a shape with no symmetry is worse than one that
          // says it cannot.
          items.push({
            id: 'mirror',
            icon: 'mirror',
            label: 'Mirror selection',
            title: ctx.points.canMirror
              ? ctx.points.mirroring
                ? 'Stop choosing across the axis'
                : 'Choose across the axis too'
              : 'This shape has no axis to mirror across',
            pressed: ctx.points.mirroring,
            disabled: !ctx.points.canMirror,
            run: () => {
              ctx.points.setMirroring(!ctx.points.mirroring);
              ctx.railChanged();
              ctx.refresh();
            },
          });
        }

        items.push({
          id: 'invert',
          icon: 'invert',
          label: 'Invert selection',
          title: points ? 'Choose the rest of the path instead' : 'Choose everything else instead',
          // Inverting nothing would quietly mean "choose everything", which is ⌘A's job and
          // not what the word says. With nothing chosen there is nothing to turn inside out.
          disabled: !chosen,
          run: () => reach(ctx, 'invert'),
        });
        return { kind: 'buttons', items };
      };

      if (mode === 'object') {
        return {
          top,
          bottom: reaching(),
        };
      }

      const preview = ctx.points.preview();
      const edit = (run: () => void) => {
        ctx.stopCapturing();
        run();
        ctx.stopCapturing();
        ctx.railChanged();
        ctx.refresh();
      };
      const helpers = reaching();
      const selected = preview?.selected.map(index => preview.points[index]!).filter(Boolean) ?? [];
      const lone = ctx.points.lone;
      return {
        title: `${ctx.points.chosenCount} ${ctx.points.chosenCount === 1 ? 'point' : 'points'} selected`,
        pointEditing: {
          selected: ctx.points.chosenCount, total: preview?.points.length ?? 0, closed: ctx.points.closed,
          coordinates: lone ? (['x', 'y'] as const).map(axis => ({
            id: axis, label: axis.toUpperCase(), title: `Point ${axis.toUpperCase()} — position on the canvas`,
            value: lone.at[axis], min: -1e7, max: 1e7, step: 0.1,
            format: value => formatLength(value, ctx.units ?? 'px', { bare: true }),
            parse: text => parseLength(text, ctx.units ?? 'px'),
            onChange: value => edit(() => { const current = ctx.points.lone; if (current) ctx.points.placeLone({ ...current.at, [axis]: value }); }),
          })) : undefined,
        },
        top,
        middle: { kind: 'buttons', items: [
          { id: 'smooth', icon: 'smooth', label: 'Smooth', title: 'Smooth — curve the selected points · Double-click an anchor to toggle', pressed: selected.length > 0 && selected.every(point => (point.kind === 'smooth' || point.kind === 'mirrored') && !!(point.in || point.out)), disabled: !ctx.points.chosenCount, run: () => edit(() => ctx.points.setSelectedKind('smooth')) },
          { id: 'corner', icon: 'corner', label: 'Corner', title: 'Corner — remove handles from the selected points · Double-click an anchor to toggle', pressed: selected.length > 0 && selected.every(point => !point.in && !point.out), disabled: !ctx.points.chosenCount, run: () => edit(() => ctx.points.setSelectedKind('corner')) },
          { id: 'add-point', icon: 'plus', label: 'Add point', title: 'Add point — click anywhere on the path to insert an anchor', pressed: addingPoints, disabled: !preview, run: () => { addingPoints = !addingPoints; mode = 'point'; ctx.railChanged(); ctx.refresh(); } },
          { id: 'close-path', icon: ctx.points.closed ? 'release' : 'join', label: ctx.points.closed ? 'Open path' : 'Close path', title: !preview || preview.focus.count < 3 ? 'Close path — add a third point first' : ctx.points.closed ? 'Open path — disconnect the first and last points' : 'Close path — connect the first and last points', disabled: !preview || preview.focus.count < 3, preview: shown => { previewEnds = shown; ctx.refresh(); }, run: () => edit(() => ctx.points.toggleClosed()) },
          { id: 'simplify-points', icon: 'magic', label: 'Simplify points', title: 'Simplify points — fewer anchors, the same shape; points on top of each other are merged', disabled: !preview || preview.points.length < 4, run: () => edit(() => ctx.points.simplify()) },
          { id: 'remove-points', icon: 'trash', label: 'Delete points', title: 'Delete points — remove the selected anchors', disabled: !ctx.points.chosenCount, run: () => edit(() => ctx.points.removeSelected()) },
        ] },
        bottom: { kind: 'buttons', items: [
          { id: 'all-points', icon: 'select-area', label: 'Select all points', title: 'Select all points on this path', disabled: !preview, run: () => { ctx.points.takeAll(); ctx.railChanged(); ctx.refresh(); } },
          ...(helpers.kind === 'buttons' ? helpers.items : []),
          { id: 'deselect-points', icon: 'close', label: 'Deselect points', title: 'Deselect points — keep editing this path', disabled: !ctx.points.chosenCount, run: () => { ctx.points.selectIndices([], false); ctx.railChanged(); ctx.refresh(); } },
        ] },
      };
    },
  };
}

/** Preview exactly the closing segment, including the end anchors' Bézier handles. */
function drawEndConnection(overlay: Overlay, ctx: ToolContext): void {
  const editing = ctx.points.preview();
  // The ends of the subpath the chosen points are on, which is the one Close path joins.
  if (!editing || editing.focus.count < 3) return;
  const first = editing.points[editing.focus.start]!;
  const last = editing.points[editing.focus.start + editing.focus.count - 1]!;
  const world = ctx.scene.worldTransform(editing.id);
  const place = (x: number, y: number) => applyToPoint(world, x, y);
  const start = place(last.x, last.y);
  const end = place(first.x, first.y);
  const out = last.out ? place(...last.out) : start;
  const inside = first.in ? place(...first.in) : end;
  overlay.curve([{ at: start, out }, { at: end, in: inside }], { width: 3 });
  overlay.handle(start, { size: 5, hovered: true });
  overlay.handle(end, { size: 5, hovered: true });
  const middle = { x: (start.x + 3 * out.x + 3 * inside.x + end.x) / 8, y: (start.y + 3 * out.y + 3 * inside.y + end.y) / 8 };
  overlay.label(middle, editing.closed ? 'Disconnect these ends' : 'Connect these ends', { size: 11, offset: { x: 0, y: -18 } });
}

/**
 * A path opened for editing: every anchor, the handles of the ones chosen, and — while
 * nothing is being dragged — the piece under the pointer with the point a click would add.
 */
function drawPoints(overlay: Overlay, ctx: ToolContext, offering: boolean, adding = false): void {
  const editing = ctx.points.preview();
  if (!editing) return;
  const world = ctx.scene.worldTransform(editing.id);
  const place = (x: number, y: number) => ({ x: world[0]! * x + world[2]! * y + world[4]!, y: world[1]! * x + world[3]! * y + world[5]! });

  // A piece of path between two chosen anchors is lit, so a run of them reads as one
  // thing rather than as several dots that happen to be near each other.
  const chosen = new Set(editing.selected);
  for (let index = 0; index < editing.points.length; index++) {
    // Along each subpath only: the last anchor of one and the first of the next are not neighbours.
    const next = ctx.points.nextOf(index);
    if (next === null) continue;
    const lit = chosen.has(index) && chosen.has(next);
    const hovered = offering && editing.segment?.index === index;
    if (!lit && !hovered) continue;
    const run = ctx.points.segmentPath(index);
    if (run) overlay.curve(run.map((at) => ({ at })), { width: lit ? 1.5 : 1 });
  }

  editing.points.forEach((point, index) => {
    const at = place(point.x, point.y);
    const isChosen = chosen.has(index);
    // Handles belong to the chosen anchors: showing them all is a thicket.
    if (isChosen) {
      for (const [side, handle] of [
        ['in', point.in],
        ['out', point.out],
      ] as const) {
        if (!handle) continue;
        const to = place(handle[0], handle[1]);
        overlay.line(at, to, { width: 1 });
        overlay.knob(to, { hovered: editing.hovered?.index === index && editing.hovered.kind === side });
      }
    }
    overlay.handle(at, {
      filled: isChosen,
      hovered: editing.hovered?.index === index && editing.hovered.kind === 'anchor',
    });
  });

  // The point a click would add, at the middle of the piece under the hand.
  if (offering && editing.segment) overlay.knob(adding ? editing.segment.at : editing.segment.middle, { hovered: true });
}
